import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type JSX,
  type ReactNode,
  type MutableRefObject,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Settings2,
  Building2,
  User,
  Shield,
  Bell,
  SlidersHorizontal,
  Cog,
  Link2,
  Database,
  FileText,
  Key,
  ClipboardList,
  Check,
  X,
  ExternalLink,
  AlertTriangle,
  Image as ImageIcon,
  Trash2,
  GripVertical,
  RotateCcw,
  Network,
  Receipt,
  Truck,
  Plug,
  Calculator,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../hooks/useApp';
import {
  useParamStore,
  PARAM_LEVELS,
  type ParamRow,
  type UseParamStoreResult,
  type ParamLevel,
} from '../hooks/useParamStore';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import { useSettingsConfig } from '../hooks/useSettingsConfig';
import { isAdminOnlySection } from '../lib/roles';
import { EVENT_LABELS, EVENT_GROUPS } from '../lib/notifications';
import type { NotificationEventGroup } from '../lib/notifications';
import { supabase } from '../lib/supabase';
import type { Factor } from '@supabase/supabase-js';
import type { Database as ArbiDatabase } from '../lib/database.types';
import ArbiSelect, { type ArbiSelectOption } from '../components/ui/ArbiSelect';
import ArbiToggle from '../components/ui/ArbiToggle';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import ProductPickerTypeahead from '../components/ui/ProductPickerTypeahead';
import { Toast, type ToastState } from '../components/ui/Toast';
import type {
  BuyingSessionPrefs,
  BuyingSessionFilterPref,
  BuyingSessionWeightPref,
  BuyScoreWeightComponent,
  IntegrationConfig,
  DocumentTemplate,
  WebhookEndpoint,
} from '../types';
import { FILTER_FIELDS, mergePrefs } from '../lib/buyingSessionDefaults';

// ── Local types ───────────────────────────────────────────────────────────────

type SectionId =
  | 'profile'
  | 'general'
  | 'security'
  | 'billing'
  | 'notifications'
  | 'params'
  | 'po-types'
  | 'rules-engine'
  | 'distributor-capabilities'
  | 'cost-line-items'
  | 'channels-and-paths'
  | 'marketplace-apis'
  | 'multichannel'
  | 'shipping'
  | 'accounting'
  | 'algopix'
  | 'repricer'
  | 'blvs-api'
  | 'keepa'
  | 'docs-templates'
  | 'api-keys'
  | 'webhooks'
  | 'audit-trail'
  | 'data';

interface PoTypeRow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  is_active: boolean;
  show_discount_pct?: boolean | null;
  show_volume_tier?: boolean | null;
  returns_allowed?: boolean | null;
  qty_minimum?: number | null;
  sort_order?: number | null;
}

type SectionRefs = MutableRefObject<Record<string, HTMLDivElement | null>>;

// ── Nav structure ─────────────────────────────────────────────────────────────

interface NavItem {
  id: SectionId;
  label: string;
  icon: LucideIcon;
}

interface NavGroupDef {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: readonly NavGroupDef[] = [
  {
    label: 'ORGANIZATION',
    items: [
      { id: 'profile', label: 'Profile', icon: User },
      { id: 'general', label: 'General', icon: Building2 },
      { id: 'security', label: 'Security', icon: Shield },
      // Day 93 P7.7 G.12 — Billing placement in ORGANIZATION per CLAUDE.md §6
      // nav (between Security and Notifications). Static informational only;
      // no substrate, no external link.
      { id: 'billing', label: 'Billing', icon: Receipt },
      { id: 'notifications', label: 'Notifications & Alerts', icon: Bell },
    ],
  },
  {
    label: 'BUSINESS RULES',
    items: [
      { id: 'params', label: 'Params & Defaults', icon: SlidersHorizontal },
      { id: 'po-types', label: 'PO Types', icon: FileText },
      { id: 'rules-engine', label: 'Rules Engine', icon: Cog },
      // Distributor Scope nav entry RETIRED — U10 SLICE B (20260719000045):
      // domain_scope_config dropped (fork 3); section removed with the table.
      // U10 A2 (BACKLOG:315 [ANNOTATED AR-0145: DEAD at HEAD; anchor by content — "U10 A2 - the per-distributor capability registry editor"]a) — per-distributor capability registry editor
      // (distributor_capability is_supported + per-direction config; the :306
      // BLVS deferred-details discharge path). Admin-only per Architecture
      // Rule 4 (capability-vs-fallback gating is engine-relevant policy).
      // Placement: per-distributor policy adjacency (Distributor Scope →
      // Distributor Capabilities).
      { id: 'distributor-capabilities', label: 'Distributor Capabilities', icon: Plug },
      // Day 87 P7.3 + D.8 + E.5=Receipt — cost components surface (Cost Line
      // Items). Admin-only per Architecture Rule 4 (cost components are
      // engine-relevant policy). Placement between rules-engine and
      // channels-and-paths in BUSINESS RULES nav.
      { id: 'cost-line-items', label: 'Cost Line Items', icon: Receipt },
      // Day 86 P7.2 (#DBN-R1-β) — Channels & Paths placement in BUSINESS RULES
      // group; this is where Day 63a Section 1+2 originally graduated FROM
      // (they're coming home from arbi-dev FormulaEditor per ADR Decision 39).
      { id: 'channels-and-paths', label: 'Channels & Paths', icon: Network },
    ],
  },
  {
    label: 'INTEGRATIONS',
    items: [
      { id: 'marketplace-apis', label: 'Marketplace APIs', icon: Link2 },
      // Day 93 P7.7 G.13/G.14/G.15 — INTEGRATIONS family scaffolds. Placement
      // matches CLAUDE.md §6 nav order: Marketplace APIs → Multi-channel Sync
      // → Shipping → Accounting → Algopix → Repricer → BLVS API → Keepa.
      { id: 'multichannel', label: 'Multi-channel Sync', icon: Network },
      { id: 'shipping', label: 'Shipping', icon: Truck },
      { id: 'accounting', label: 'Accounting', icon: Calculator },
      { id: 'algopix', label: 'Algopix', icon: Link2 },
      { id: 'repricer', label: 'Repricer', icon: Link2 },
      { id: 'blvs-api', label: 'BLVS API', icon: Link2 },
      { id: 'keepa', label: 'Keepa', icon: Link2 },
    ],
  },
  {
    label: 'SYSTEM',
    items: [
      { id: 'docs-templates', label: 'Documents & Templates', icon: FileText },
      { id: 'api-keys', label: 'API Keys', icon: Key },
      // Day 93 P7.7 G.16 — Webhooks placement in SYSTEM between API Keys and
      // Audit Trail per CLAUDE.md §6 nav order.
      { id: 'webhooks', label: 'Webhooks', icon: Webhook },
      { id: 'audit-trail', label: 'Audit Trail', icon: ClipboardList },
      { id: 'data', label: 'Data', icon: Database },
    ],
  },
];

// ── Param formatting helpers ──────────────────────────────────────────────────

const PARAM_LABELS: Record<string, string> = {
  min_margin_pct: 'Minimum Margin',
  min_roi_pct: 'Minimum ROI',
  safety_buffer_pct: 'Safety Buffer',
  returns_reserve_pct: 'Returns Reserve',
  default_returns_rate: 'Default Returns Rate',
  amazon_payout_days: 'Amazon Payout Cycle',
  ebay_payout_days: 'eBay Payout Cycle',
  walmart_payout_days: 'Walmart Payout Cycle',
  staleness_yellow_days: 'Data Stale Warning',
  staleness_red_days: 'Data Stale Critical',
  amazon_storage_fee_normal: 'FBA Storage (Normal)',
  amazon_storage_fee_q4: 'FBA Storage (Q4)',
  fba_prep_fee_default: 'FBA Prep Fee',
  fbm_shipping_per_unit: 'FBM Shipping Per Unit',
  ebay_fee_rate: 'eBay Fee Rate',
  ebay_listing_fee: 'eBay Listing Fee',
  walmart_referral_fee_rate: 'Walmart Referral Fee Rate',
  walmart_wfs_fee_default: 'Walmart WFS Fee',
  shopify_payment_fee_rate: 'Shopify Payment Fee Rate',
  shopify_payment_fixed_fee: 'Shopify Payment Fixed Fee',
  inbound_shipping_per_unit: 'Inbound Shipping Per Unit',
  supplier_shipping_per_unit: 'Supplier Shipping Per Unit',
  misc_fee_per_unit: 'Misc Fee Per Unit',
  shopify_payout_days: 'Shopify Payout Cycle',
  // S-F3/F4 fix (Fix Pass A) — dropship_payout_days IS engine-read
  // (calculate_profitability dropship path) but was unlabeled → fell to the
  // "Other Parameters" catch-all (invisible in entity-override mode). Labeled +
  // grouped with the other {channel}_payout_days (Timing group) below.
  dropship_payout_days: 'Dropship Payout Cycle',
  shopify_markup_factor: 'Shopify Price Markup Factor',
  days_to_sell_default: 'Default Days to Sell',
  credit_util_warning_pct: 'Credit Utilization Warning',
  credit_util_danger_pct: 'Credit Utilization Danger',
  credit_available_danger_pct: 'Available Credit Danger',
  // Day 86 P7.2 — Engine Tuning subsection (2 D.2 surviving keys per A6)
  default_lead_time_days: 'Default Lead Time',
  invoice_variance_tolerance_pct: 'Invoice Variance Tolerance',
  // Day 86 P7.2 — Fulfillment Costs L467 cluster (4 FBA-specific per-unit fees per A3)
  aged_inventory_surcharge: 'Aged Inventory Surcharge',
  inbound_placement_fee: 'Inbound Placement Fee',
  low_inventory_level_fee: 'Low Inventory Level Fee',
  inbound_defect_fee: 'Inbound Defect Fee',
};

interface ParamGroup {
  label: string;
  keys: string[];
  // Day 86 P7.2 — optional subsection-level guidance per TONE_STANDARDS §9.
  // Renders as text-xs ink-muted caption below the group header. Used for
  // change-with-care subsections (Engine Tuning per Finding 7 framing).
  caption?: string;
  // Day 88b CF 64 (supersedes Day 87 P7.3 `supportsDistributorOverride?: boolean`).
  // Per-group DEFAULT supported levels for entity-mode UI visibility.
  // Omitted = all 6 levels (default — Margins, Returns).
  // Explicit array = subset of levels operator can override at.
  // Per-key overrides via KEYS_META map below (handles mixed-scope groups
  // where individual keys deviate from group default).
  // Disposition per Sol Checkpoint #1 §A1 9-key table (Day 88a Phase 4 lock).
  supportedLevels?: ParamLevel[];
}

const PARAM_GROUPS_SETTINGS: readonly ParamGroup[] = [
  {
    // Day 88b per Sol §A1 lock — all 3 keys full-6-level; group default undefined.
    label: 'Margins & Profitability',
    keys: ['min_margin_pct', 'min_roi_pct', 'safety_buffer_pct'],
  },
  {
    // Day 88b Phase 3 + Sol §A1 lock per #DBN-γ Option I — 5 cost_line_items-storage
    // keys REMOVED from this group (fba_prep_fee_default + fbm_shipping_per_unit +
    // inbound_shipping_per_unit + supplier_shipping_per_unit + misc_fee_per_unit).
    // Engine reads these from public.cost_line_items table; pre-Phase-3 entries in
    // PARAM_GROUPS_SETTINGS were dead-write (operator edits went to params shadow
    // rows that engine never read). Day 87 D.8 CostLineItemsSection is the canonical
    // edit surface for these 5 keys. Phase 4 DELETE migration removes the 5 shadow
    // rows from prod params at business level.
    //
    // Remaining 6 keys (amazon_storage * 2 + L467 cluster * 4) are engine-unread
    // ($0 sentinel default surfaces configurability without behavior change per
    // Day 86 P7.2 A3+A6). Group default ['business'] reflects engine-unread state;
    // operator can still adjust business defaults via this UI for future engine
    // wiring (post-launch work).
    // K4 supersedes R4-(i) (2026-06-23): engine-unread → captioned (trust-bug
    // closure); the future-tunable intent is preserved via the "(post-launch)" clause.
    label: 'Fulfillment Costs',
    // K4 (ghost-config) — user-facing group caption; all 6 keys are engine-unread
    // ($0 sentinel) with no consumer, so a whole-group caption is honest here
    // (unlike the mixed Timing group, where staleness uses a per-key hint instead).
    caption: 'Reference only — not yet used in profit calculations (post-launch).',
    keys: [
      'amazon_storage_fee_normal',
      'amazon_storage_fee_q4',
      // Day 86 P7.2 + A3 + A6 — L467 4-key FBA fee cluster (per-unit USD;
      // $0 sentinel default = "not yet configured"; engine treats unparameterized
      // as $0 today). Routine-tunable as Amazon fee schedule shifts.
      'aged_inventory_surcharge',
      'inbound_placement_fee',
      'low_inventory_level_fee',
      'inbound_defect_fee',
    ],
    supportedLevels: ['business'],
  },
  {
    // Day 88b per Sol §A1 lock — both keys full-6-level; group default undefined.
    label: 'Returns',
    keys: ['returns_reserve_pct', 'default_returns_rate'],
  },
  {
    // Day 88b per Sol §A1 lock — 6 channel-fixed keys take group default
    // ['business', 'distributor']; shopify_markup_factor is full-6-level
    // (operator-side cost→sale price markup) — per-key override via KEYS_META below.
    label: 'Marketplace Fees',
    keys: [
      'ebay_fee_rate',
      'ebay_listing_fee',
      'walmart_referral_fee_rate',
      'walmart_wfs_fee_default',
      'shopify_payment_fee_rate',
      'shopify_payment_fixed_fee',
      'shopify_markup_factor',
    ],
    supportedLevels: ['business', 'distributor'],
  },
  {
    // Day 88b per Sol §A1 lock — 4 channel-fixed payout keys take group default
    // ['business', 'distributor']; days_to_sell_default full-6-level + 2 staleness
    // keys business-only — per-key overrides via KEYS_META below.
    label: 'Timing',
    keys: [
      'amazon_payout_days',
      'ebay_payout_days',
      'walmart_payout_days',
      'shopify_payout_days',
      // Fix Pass A (S-F3/F4) — dropship channel payout, engine-read, channel-fixed
      // like its siblings → group default ['business', 'distributor'].
      'dropship_payout_days',
      'days_to_sell_default',
      'staleness_yellow_days',
      'staleness_red_days',
    ],
    supportedLevels: ['business', 'distributor'],
  },
  {
    // Day 88b per Sol §A1 lock — all 3 credit threshold keys are UI-display only
    // (engine-unread; engine has no concept of credit utilization warnings).
    // Business-only.
    label: 'Credit Thresholds',
    keys: ['credit_util_warning_pct', 'credit_util_danger_pct', 'credit_available_danger_pct'],
    supportedLevels: ['business'],
  },
  // Day 86 P7.2 + A6 — Engine Tuning subsection. 2 D.2 surviving keys after the
  // strict-deletion of restock_urgency_red_lead_multiplier + restock_urgency_yellow_lead_multiplier
  // at Day 86 migration §4 (inlined as literals in forecast_restock body §6 per
  // A5 Form 1). Per Audit #2 Finding 7 — change-with-care posture; caption renders
  // the warning copy per R3 Variant A.
  // Day 87 P7.3 + A2=c locked supportsDistributorOverride: false per Day 86 §4
  // strict deletion intent; Day 88a/88b lifts to intermediate-scope per Sol §A1
  // 9-key table — these 2 keys are operator-eligible at manufacturer level (problem
  // manufacturers with known invoice variance / divergent lead times). default_lead_time_days
  // additionally has column-primary fallback semantic preserved in forecast_restock body
  // (s.lead_time_days dominates over rpp.default_lead_time_days per Day 71 P3.4 +
  // Day 88a P2.3 sub-case (a) preservation).
  {
    label: 'Engine Tuning',
    keys: ['default_lead_time_days', 'invoice_variance_tolerance_pct'],
    caption:
      'Engine-internal constants. Changes affect Restock Intelligence urgency tiers and invoice variance flagging.',
    supportedLevels: ['business', 'distributor', 'manufacturer'],
  },
];

// Day 88b CF 64 per-key supportedLevels overrides where individual keys deviate
// from the per-group default declared above. Per Sol §A1 9-key table (Day 88a
// Phase 4 lock). Lookup via effectiveSupportedLevels(group, key) helper below —
// per-key override wins → per-group default → all-6-levels default.
const KEYS_META: Partial<Record<string, { supportedLevels?: ParamLevel[] }>> = {
  // Marketplace Fees per-key override — full-6-level (group default is channel-fixed)
  shopify_markup_factor: { supportedLevels: undefined },
  // Timing per-key overrides
  days_to_sell_default: { supportedLevels: undefined }, // full-6-level (group default is channel-fixed payout)
  staleness_yellow_days: { supportedLevels: ['business'] }, // UI-display threshold; engine-unread
  staleness_red_days: { supportedLevels: ['business'] }, // UI-display threshold; engine-unread
};

// K4 (ghost-config) — per-key inert-control hints rendered under the param label
// in InlineParamRow (text-2xs, the row.notes idiom). Contains ONLY the two
// staleness display-threshold keys: editable in business mode but engine-unread
// AND with no UI consumer (the live staleness display reads
// manual_override_stale_days, not these). Captioned so a reviewer isn't misled
// into thinking an edit changes behavior. Add a key here only if it is a
// genuinely inert editable param.
const KEY_HINTS: Partial<Record<string, string>> = {
  staleness_yellow_days: 'Display threshold reserved — not yet wired.',
  staleness_red_days: 'Display threshold reserved — not yet wired.',
};

// Day 88b — Returns effective supportedLevels for a key within a group.
// Precedence: per-key KEYS_META override → per-group default → all 6 levels.
//
// Per-mode visibility contract per Sol §A1 disposition (operator-facing UX):
// - business mode:    All 7 groups visible (baseline; no override semantic)
// - distributor mode: Margins / Returns / Marketplace Fees / Timing / Engine Tuning
//                     visible; Credit Thresholds + Fulfillment Costs hidden
//                     (their keys are ['business']-only)
// - manufacturer mode: Margins / Returns / Engine Tuning visible + partial
//                     Marketplace Fees (shopify_markup_factor only via KEYS_META
//                     undefined → all 6) + partial Timing (days_to_sell_default
//                     only via KEYS_META undefined → all 6)
// - category mode:    same as manufacturer minus Engine Tuning (Engine Tuning is
//                     intermediate ['business', 'distributor', 'manufacturer'])
// - brand mode:       same as category
// - product mode:     UI deferred to Day 88.5 per Lock #6 (engine substrate
//                     supports product-level resolution via Day 88a Phase 1
//                     6-level walk; product picker UX is the only gap)
function effectiveSupportedLevels(group: ParamGroup, key: string): ParamLevel[] {
  const meta = KEYS_META[key];
  if (meta && 'supportedLevels' in meta) {
    // Explicit per-key entry — may be undefined (= all 6) OR explicit array
    return meta.supportedLevels ?? (PARAM_LEVELS as readonly ParamLevel[] as ParamLevel[]);
  }
  return group.supportedLevels ?? (PARAM_LEVELS as readonly ParamLevel[] as ParamLevel[]);
}

// Day 88b — Returns true if the group has at least one key that supports the
// given entity level. Used by consumer logic to filter visible groups by active
// entity mode (e.g., hide Credit Thresholds when distributor mode is active
// since all its keys are business-only).
function groupSupportsLevel(group: ParamGroup, level: ParamLevel): boolean {
  return group.keys.some((key) => effectiveSupportedLevels(group, key).includes(level));
}

function formatParamValue(key: string, value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '\u2014';
  if (key.endsWith('_pct')) return `${value}%`;
  if (key.endsWith('_days') || key === 'days_to_sell_default') return `${value} days`;
  if (key.endsWith('_rate')) return `${(Number(value) * 100).toFixed(1)}%`;
  if (key.endsWith('_factor')) return `×${Number(value).toFixed(2)}`;
  if (
    key.includes('_fee') ||
    key.includes('_storage') ||
    key.includes('_shipping') ||
    key.includes('_cost') ||
    key.includes('_surcharge') // Day 86 P7.2 — aged_inventory_surcharge per A3 bare names
  )
    return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return String(value);
}

function parseParamInput(key: string, raw: string): number | string | null {
  const trimmed = raw.trim().replace(/[$%]/, '').replace(' days', '');
  if (key.endsWith('_rate')) {
    const pct = parseFloat(trimmed);
    return isNaN(pct) ? null : pct / 100;
  }
  const n = parseFloat(trimmed);
  return isNaN(n) ? trimmed : n;
}

function inputDisplayValue(key: string, value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (key.endsWith('_rate')) return String(Number(value) * 100);
  return String(value);
}

function getParamLabel(param: ParamRow): string {
  return PARAM_LABELS[param.key] || param.label || param.key;
}

function getInputPlaceholder(key: string): string {
  if (key.endsWith('_pct')) return 'e.g. 15 for 15%';
  if (key.endsWith('_days') || key === 'days_to_sell_default') return 'Number of days';
  if (key.endsWith('_rate')) return 'e.g. 5 for 5%';
  if (key.endsWith('_factor')) return 'e.g. 1.5 for 150%';
  if (
    key.includes('_fee') ||
    key.includes('_storage') ||
    key.includes('_shipping') ||
    key.includes('_cost') ||
    key.includes('_surcharge') // Day 86 P7.2 — aged_inventory_surcharge per A3 bare names
  )
    return 'e.g. 0.55';
  return '';
}

// ── Section card wrapper ──────────────────────────────────────────────────────

interface SectionCardProps {
  id: SectionId;
  title: string;
  children: ReactNode;
  sectionRefs: SectionRefs;
}

function SectionCard({ id, title, children, sectionRefs }: SectionCardProps): JSX.Element {
  return (
    <div
      id={`section-${id}`}
      ref={(el) => {
        if (sectionRefs) sectionRefs.current[id] = el;
      }}
      className="bg-surface-1 border border-border rounded-lg"
    >
      <div className="px-4 py-3 border-b border-border-subtle">
        <h2 className="text-sm font-semibold text-ink-bright">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// ── Stub section (empty state) ────────────────────────────────────────────────

interface StubSectionProps {
  id: SectionId;
  title: string;
  icon: LucideIcon;
  message: string;
  sectionRefs: SectionRefs;
}

function StubSection({
  id,
  title,
  icon: Icon,
  message,
  sectionRefs,
}: StubSectionProps): JSX.Element {
  return (
    <SectionCard id={id} title={title} sectionRefs={sectionRefs}>
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Icon size={48} className="text-ink-muted mb-3" />
        <p className="text-sm text-ink-muted">{message}</p>
      </div>
    </SectionCard>
  );
}

// ── Inline editable param row ─────────────────────────────────────────────────

interface InlineParamRowProps {
  param: ParamRow;
  onSave: (id: string, value: number | string | null, reason: string) => Promise<void>;
}

function InlineParamRow({ param, onSave }: InlineParamRowProps): JSX.Element {
  const [editing, setEditing] = useState<boolean>(false);
  const [editVal, setEditVal] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  function startEdit(): void {
    setEditVal(inputDisplayValue(param.key, param.value));
    setErr(null);
    setEditing(true);
  }

  function cancel(): void {
    setEditing(false);
    setErr(null);
  }

  async function save(): Promise<void> {
    const parsed = parseParamInput(param.key, editVal);
    if (parsed === null) {
      setErr('Invalid value');
      return;
    }
    setBusy(true);
    try {
      await onSave(param.id, parsed, '');
      setEditing(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
    }
    setBusy(false);
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    }
    if (e.key === 'Escape') cancel();
  }

  // K4 (ghost-config) — per-key inert hint; KEY_HINTS holds ONLY the two staleness
  // keys, so every live Timing param (payout-days, days_to_sell_default) resolves
  // to undefined here and renders unchanged (no hint, no layout shift).
  const hint = KEY_HINTS[param.key];

  return (
    <div className="flex items-center justify-between py-2.5 px-1 group hover:bg-surface-3 rounded-md transition-colors">
      <div>
        <div className="text-sm text-ink-primary">{getParamLabel(param)}</div>
        {hint && <p className="text-2xs text-ink-muted mt-0.5">{hint}</p>}
      </div>
      <div className="w-48 text-right">
        {editing ? (
          <div className="space-y-1.5">
            <input
              ref={inputRef}
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onKeyDown={handleKeyDown}
              className="arbi-input w-full text-sm text-right"
              placeholder={getInputPlaceholder(param.key)}
            />
            {err && <p className="text-xs text-danger text-right">{err}</p>}
            <div className="flex justify-end gap-1">
              <button onClick={save} disabled={busy} className="arbi-btn-primary text-xs px-2 py-1">
                <Check size={11} /> Save
              </button>
              <button
                onClick={cancel}
                className="arbi-btn-ghost text-xs px-2 py-1"
                aria-label="Cancel"
              >
                <X size={11} />
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={startEdit}
            className="text-sm font-mono text-ink-bright hover:text-accent transition-colors cursor-pointer"
            title="Click to edit"
          >
            {formatParamValue(param.key, param.value)}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Inline param row with base + override semantics (Day 87 P7.3 + A1=a) ─────
// Lifted inline from apps/arbi-dev/src/pages/FormulaEditor.tsx L319-499
// per A1=a — single-file consumer post-Day-87 (FormulaEditor's copy goes away
// in same build day per Phase 3 emptying). Uses Settings' format helpers
// (parseParamInput / formatParamValue / inputDisplayValue / getInputPlaceholder)
// for consistency with InlineParamRow (business mode); FormulaEditor's simpler
// parseValue/formatValue not lifted. Visual style matches InlineParamRow shape
// per Settings conventions. Explicit "Remove" button surfaces D.11 spec's
// "remove override" affordance alongside the implicit empty-draft-clear
// semantic (both deletePram against overrideRow.id; UX-equivalent).

// Day 88b — entity mode levels that the override row UI supports.
// Excludes 'business' (no override; that's the base).
// Day 88.5 P1 extends to 5-value union — adds 'product'. UI substrate via
// ProductPickerTypeahead (P2) for 1955+ row scaling per Sol Lock #6 fulfillment.
type EntityModeLevel = 'distributor' | 'manufacturer' | 'category' | 'brand' | 'product';

interface InlineParamOverrideRowProps {
  paramKey: string;
  store: UseParamStoreResult;
  // Day 88b generalization — distributorId → entityLevel + entityId per Sol §A1
  // 9-key table CF 64 UI substrate. Override row reads + writes at the level
  // entity_id pair instead of hardcoded distributor-only.
  entityLevel: EntityModeLevel;
  entityId: string | null;
  onSaved: () => void;
  onError: (msg: string) => void;
}

function InlineParamOverrideRow({
  paramKey,
  store,
  entityLevel,
  entityId,
  onSaved,
  onError,
}: InlineParamOverrideRowProps): JSX.Element {
  // p.marketplace = params.marketplace, the enum-typed stored column (NULL = the
  // path-unscoped base row; the stored name renames only when the enum dies,
  // slice-B family).
  const baseRow: ParamRow | undefined = store
    .getByLevel('business')
    .find((p) => p.key === paramKey && !p.marketplace);
  const overrideRow: ParamRow | undefined = entityId
    ? store.getByLevel(entityLevel, entityId).find((p) => p.key === paramKey && !p.marketplace)
    : undefined;

  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const baseDisplay = baseRow ? formatParamValue(paramKey, baseRow.value) : '—';
  const overrideDisplay = overrideRow ? formatParamValue(paramKey, overrideRow.value) : '';
  const label = PARAM_LABELS[paramKey] ?? paramKey;

  function startEdit(): void {
    setDraft(overrideRow ? inputDisplayValue(paramKey, overrideRow.value) : '');
    setErr(null);
    setEditing(true);
  }

  function cancel(): void {
    setEditing(false);
    setDraft('');
    setErr(null);
  }

  async function save(): Promise<void> {
    if (!entityId) {
      onError(`No ${entityLevel} selected`);
      return;
    }
    const trimmed = draft.trim();
    setBusy(true);
    setErr(null);
    try {
      // Empty draft = clear override (delete entity-level row, fall back to base).
      if (!trimmed) {
        if (overrideRow) {
          await store.deleteParam(overrideRow.id);
          onSaved();
        }
        setEditing(false);
        setDraft('');
        setBusy(false);
        return;
      }
      const parsed = parseParamInput(paramKey, trimmed);
      if (parsed === null) {
        setErr('Invalid value');
        setBusy(false);
        return;
      }
      if (overrideRow) {
        await store.updateParam(overrideRow.id, parsed, '');
      } else {
        await store.insertParam({
          key: paramKey,
          value: parsed,
          level: entityLevel,
          entity_id: entityId,
          // params.marketplace stored column (enum-typed; NULL = path-unscoped).
          marketplace: null,
        });
      }
      setEditing(false);
      setDraft('');
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
      onError(msg);
    }
    setBusy(false);
  }

  async function removeOverride(): Promise<void> {
    if (!overrideRow) return;
    setBusy(true);
    try {
      await store.deleteParam(overrideRow.id);
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      onError(msg);
    }
    setBusy(false);
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    }
    if (e.key === 'Escape') cancel();
  }

  return (
    <div className="flex items-center justify-between py-2.5 px-1 group hover:bg-surface-3 rounded-md transition-colors">
      <div className="flex-1">
        <div className="text-sm text-ink-primary">{label}</div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right text-2xs text-ink-muted">
          <div className="uppercase tracking-wider">Base</div>
          <div className="font-mono text-ink-secondary">{baseDisplay}</div>
        </div>
        <div className="text-right w-56">
          <div className="text-2xs uppercase tracking-wider text-ink-muted">Override</div>
          {editing ? (
            <div className="space-y-1.5 mt-1">
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                className="arbi-input w-full text-sm text-right"
                placeholder={overrideRow ? '(empty = clear)' : getInputPlaceholder(paramKey)}
              />
              {err && <p className="text-xs text-danger text-right">{err}</p>}
              <div className="flex justify-end gap-1">
                <button
                  onClick={save}
                  disabled={busy}
                  className="arbi-btn-primary text-xs px-2 py-1"
                >
                  <Check size={11} /> Save
                </button>
                <button
                  onClick={cancel}
                  className="arbi-btn-ghost text-xs px-2 py-1"
                  aria-label="Cancel"
                >
                  <X size={11} />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2 mt-1">
              <button
                onClick={startEdit}
                className="text-sm font-mono text-ink-bright hover:text-accent transition-colors cursor-pointer"
                title="Click to edit override"
              >
                {overrideDisplay || <span className="text-ink-muted italic">(inherit)</span>}
              </button>
              {overrideRow && (
                <button
                  onClick={removeOverride}
                  disabled={busy}
                  className="arbi-btn-ghost text-2xs px-1.5 py-0.5"
                  title="Remove override (revert to base value)"
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ORGANIZATION > Profile ────────────────────────────────────────────────────
// "You, the user." Account, password, and per-user Buying Session preferences.
// Visible to all roles (each user manages their own).

interface ProfileSectionProps {
  sectionRefs: SectionRefs;
  showToast: (t: ToastState) => void;
}

function ProfileSection({ sectionRefs, showToast }: ProfileSectionProps): JSX.Element {
  const { user, fullName, setFullName, buyingSessionPrefs, setBuyingSessionPrefs } = useApp();
  // Day 91a.5 P2C(ii) — Surface 3 part 2 bswc graduation per R-Lock-5.
  // ProfileSection's first useSettingsConfig consumer (Settings.tsx consumer
  // sites: CostLineItemsSection + POTypesSection + this ProfileSection site;
  // DomainScopeSection retired at the U10 slice-B dsc drop, 20260719000045).
  // bswcActive is the is_available=true filter per R-Lock-7 cascade; mirrors
  // P2B ParamsSidebar bswcActive precedent verbatim.
  const { buyScoreWeightComponents } = useSettingsConfig();
  const bswcActive = useMemo<BuyScoreWeightComponent[]>(
    () => buyScoreWeightComponents.filter((r) => r.is_available),
    [buyScoreWeightComponents]
  );

  // ── A. Account: read-only email + editable full_name ─────
  const [nameDraft, setNameDraft] = useState<string>(fullName ?? '');
  const [nameSaving, setNameSaving] = useState<boolean>(false);
  // Sync draft if fullName changes after mount (e.g. profile refetch)
  useEffect(() => {
    setNameDraft(fullName ?? '');
  }, [fullName]);
  const nameDirty = nameDraft.trim() !== (fullName ?? '').trim();

  async function saveFullName(): Promise<void> {
    if (!user?.id) return;
    if (!nameDraft.trim()) {
      showToast({ kind: 'error', message: 'Name cannot be empty' });
      return;
    }
    setNameSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ full_name: nameDraft.trim() } as never)
      .eq('id', user.id);
    setNameSaving(false);
    if (error) {
      showToast({ kind: 'error', message: `Save failed — ${error.message}` });
      return;
    }
    // Day 76 P5.1 Phase 8A — immediate AppContext sync so TopBar greeting +
    // Dashboard greeting reflect the new name without requiring a page
    // reload (closes Day 56+ BACKLOG follow-up). Previously the comment
    // here said "fullName re-syncs on next profile fetch" — now the
    // setFullName call below makes that re-sync instant.
    setFullName(nameDraft.trim());
    showToast({ kind: 'success', message: 'Name saved' });
  }

  // ── B. Password: reset link + change in place ─────────────
  const [resetSending, setResetSending] = useState<boolean>(false);
  const [pwCurrent, setPwCurrent] = useState<string>('');
  const [pwNew, setPwNew] = useState<string>('');
  const [pwConfirm, setPwConfirm] = useState<string>('');
  const [pwSaving, setPwSaving] = useState<boolean>(false);
  const [pwErr, setPwErr] = useState<string | null>(null);

  async function sendResetLink(): Promise<void> {
    if (!user?.email) return;
    setResetSending(true);
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetSending(false);
    if (error) {
      showToast({ kind: 'error', message: `Reset link failed — ${error.message}` });
      return;
    }
    showToast({ kind: 'success', message: `Reset link sent to ${user.email}` });
  }

  async function changePassword(): Promise<void> {
    setPwErr(null);
    if (!user?.email) return;
    if (pwNew.length < 8) {
      setPwErr('New password must be at least 8 characters');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwErr('New password and confirmation do not match');
      return;
    }
    setPwSaving(true);
    // Verify current via re-auth
    const { error: signinErr } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: pwCurrent,
    });
    if (signinErr) {
      setPwSaving(false);
      setPwErr('Current password is incorrect');
      return;
    }
    const { error: updateErr } = await supabase.auth.updateUser({ password: pwNew });
    setPwSaving(false);
    if (updateErr) {
      setPwErr(`Update failed — ${updateErr.message}`);
      return;
    }
    setPwCurrent('');
    setPwNew('');
    setPwConfirm('');
    showToast({ kind: 'success', message: 'Password updated' });
  }

  // ── C. Buying Session Preferences ─────────────────────────
  // Hydrate working copy from profile (or defaults if NULL).
  const initialFilters = useCallback((): BuyingSessionFilterPref[] => {
    if (buyingSessionPrefs?.filters?.length) {
      // Preserve only known + available keys; drop stale / hidden (Day 95 H.4
      // min_bsr available:false) entries silently.
      const known = new Set(FILTER_FIELDS.filter((f) => f.available !== false).map((f) => f.key));
      return buyingSessionPrefs.filters.filter((f) => known.has(f.key));
    }
    return FILTER_FIELDS.filter((f) => f.available !== false).map((f, i) => ({
      key: f.key,
      visible: true,
      default: null,
      order: i,
    }));
  }, [buyingSessionPrefs]);

  const initialWeights = useCallback((): BuyingSessionWeightPref[] => {
    if (buyingSessionPrefs?.weights?.length) {
      const known = new Set(bswcActive.map((w) => w.key));
      return buyingSessionPrefs.weights.filter((w) => known.has(w.key));
    }
    return bswcActive.map((w, i) => ({
      key: w.key,
      visible: true,
      default: null,
      order: i,
    }));
  }, [buyingSessionPrefs, bswcActive]);

  const [filterPrefs, setFilterPrefs] = useState<BuyingSessionFilterPref[]>(initialFilters);
  const [weightPrefs, setWeightPrefs] = useState<BuyingSessionWeightPref[]>(initialWeights);
  const [prefsSaving, setPrefsSaving] = useState<boolean>(false);

  // Re-hydrate when AppContext prefs change (e.g. login restore).
  useEffect(() => {
    setFilterPrefs(initialFilters());
    setWeightPrefs(initialWeights());
  }, [initialFilters, initialWeights]);

  // HTML5 native drag handling. dragKey holds the key being dragged; on drop
  // over a target row, we splice the dragged item into the target's position.
  const [dragKey, setDragKey] = useState<string | null>(null);

  function reorderFilters(srcKey: string, destKey: string): void {
    setFilterPrefs((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const srcIdx = sorted.findIndex((f) => f.key === srcKey);
      const destIdx = sorted.findIndex((f) => f.key === destKey);
      if (srcIdx < 0 || destIdx < 0 || srcIdx === destIdx) return prev;
      const [moved] = sorted.splice(srcIdx, 1);
      sorted.splice(destIdx, 0, moved);
      return sorted.map((f, i) => ({ ...f, order: i }));
    });
  }

  function reorderWeights(srcKey: string, destKey: string): void {
    setWeightPrefs((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const srcIdx = sorted.findIndex((w) => w.key === srcKey);
      const destIdx = sorted.findIndex((w) => w.key === destKey);
      if (srcIdx < 0 || destIdx < 0 || srcIdx === destIdx) return prev;
      const [moved] = sorted.splice(srcIdx, 1);
      sorted.splice(destIdx, 0, moved);
      return sorted.map((w, i) => ({ ...w, order: i }));
    });
  }

  function setFilterVisible(key: string, visible: boolean): void {
    setFilterPrefs((prev) => prev.map((f) => (f.key === key ? { ...f, visible } : f)));
  }
  function setFilterDefault(key: string, raw: string): void {
    const num = raw.trim() === '' ? null : Number(raw);
    if (raw.trim() !== '' && !Number.isFinite(num)) return; // ignore non-numeric input
    setFilterPrefs((prev) => prev.map((f) => (f.key === key ? { ...f, default: num } : f)));
  }
  function setWeightVisible(key: string, visible: boolean): void {
    setWeightPrefs((prev) => prev.map((w) => (w.key === key ? { ...w, visible } : w)));
  }
  function setWeightDefault(key: string, raw: string): void {
    const num = raw.trim() === '' ? null : Number(raw);
    if (raw.trim() !== '' && !Number.isFinite(num)) return;
    setWeightPrefs((prev) => prev.map((w) => (w.key === key ? { ...w, default: num } : w)));
  }

  async function savePrefs(): Promise<void> {
    if (!user?.id) return;
    // Day 98.5 F5 — full-prefs spread via mergePrefs so editing filter/weight
    // visibility never clobbers saved constraints/guardrails/risk/restock.
    const next: BuyingSessionPrefs = mergePrefs(buyingSessionPrefs, {
      filters: filterPrefs,
      weights: weightPrefs,
    });
    setPrefsSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ buying_session_prefs: next as unknown as never } as never)
      .eq('id', user.id);
    setPrefsSaving(false);
    if (error) {
      showToast({ kind: 'error', message: `Save failed — ${error.message}` });
      return;
    }
    setBuyingSessionPrefs(next);
    showToast({ kind: 'success', message: 'Buying Session preferences saved' });
  }

  async function resetPrefs(): Promise<void> {
    if (!user?.id) return;
    setPrefsSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ buying_session_prefs: null } as never)
      .eq('id', user.id);
    setPrefsSaving(false);
    if (error) {
      showToast({ kind: 'error', message: `Reset failed — ${error.message}` });
      return;
    }
    setBuyingSessionPrefs(null);
    setFilterPrefs(
      FILTER_FIELDS.filter((f) => f.available !== false).map((f, i) => ({
        key: f.key,
        visible: true,
        default: null,
        order: i,
      }))
    );
    setWeightPrefs(
      bswcActive.map((w, i) => ({ key: w.key, visible: true, default: null, order: i }))
    );
    showToast({ kind: 'success', message: 'Preferences reset to defaults' });
  }

  const filterLabelMap = new Map(FILTER_FIELDS.map((f) => [f.key, f]));
  const weightLabelMap = new Map(bswcActive.map((w) => [w.key, w]));

  return (
    <SectionCard id="profile" title="Profile" sectionRefs={sectionRefs}>
      <div className="space-y-8">
        {/* A. Account */}
        <div>
          <h3 className="text-xs font-semibold text-ink-bright uppercase tracking-wider mb-3">
            Account
          </h3>
          <div className="space-y-3 max-w-md">
            <div>
              <span className="block text-xs font-medium text-ink-secondary mb-1.5">Email</span>
              <p className="arbi-input w-full text-sm bg-surface-3/40 border-border-subtle text-ink-muted font-mono">
                {user?.email ?? '—'}
              </p>
              <p className="text-xs text-ink-muted mt-1">Email change requires admin assistance.</p>
            </div>
            <div>
              <label
                htmlFor="profile-full-name"
                className="block text-xs font-medium text-ink-secondary mb-1.5"
              >
                Full Name
              </label>
              <input
                id="profile-full-name"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                className="arbi-input w-full text-sm"
                placeholder="Jane Doe"
              />
            </div>
            {nameDirty && (
              <div className="flex justify-end">
                <button
                  onClick={saveFullName}
                  disabled={nameSaving}
                  className="arbi-btn-primary text-sm disabled:opacity-60"
                >
                  <Check size={14} /> {nameSaving ? 'Saving…' : 'Save name'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* B. Password */}
        <div className="pt-6 border-t border-border-subtle">
          <h3 className="text-xs font-semibold text-ink-bright uppercase tracking-wider mb-3">
            Password
          </h3>
          <div className="max-w-md space-y-4">
            <div>
              <button
                onClick={sendResetLink}
                disabled={resetSending || !user?.email}
                className="arbi-btn text-sm disabled:opacity-60"
              >
                {resetSending ? 'Sending…' : 'Send reset link to email'}
              </button>
              <p className="text-xs text-ink-muted mt-1.5">
                Sends a one-time link to {user?.email ?? 'your email'}.
              </p>
            </div>
            <div className="pt-3 border-t border-border-subtle">
              <p className="text-xs text-ink-secondary mb-3">Or change in place:</p>
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="profile-pw-current"
                    className="block text-xs font-medium text-ink-secondary mb-1"
                  >
                    Current password
                  </label>
                  <input
                    id="profile-pw-current"
                    type="password"
                    value={pwCurrent}
                    onChange={(e) => setPwCurrent(e.target.value)}
                    className="arbi-input w-full text-sm"
                    autoComplete="current-password"
                  />
                </div>
                <div>
                  <label
                    htmlFor="profile-pw-new"
                    className="block text-xs font-medium text-ink-secondary mb-1"
                  >
                    New password
                  </label>
                  <input
                    id="profile-pw-new"
                    type="password"
                    value={pwNew}
                    onChange={(e) => setPwNew(e.target.value)}
                    className="arbi-input w-full text-sm"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label
                    htmlFor="profile-pw-confirm"
                    className="block text-xs font-medium text-ink-secondary mb-1"
                  >
                    Confirm new password
                  </label>
                  <input
                    id="profile-pw-confirm"
                    type="password"
                    value={pwConfirm}
                    onChange={(e) => setPwConfirm(e.target.value)}
                    className="arbi-input w-full text-sm"
                    autoComplete="new-password"
                  />
                </div>
                {pwErr && <p className="text-xs text-danger">{pwErr}</p>}
                <div className="flex justify-end">
                  <button
                    onClick={changePassword}
                    disabled={pwSaving || !pwCurrent || !pwNew || !pwConfirm}
                    className="arbi-btn-primary text-sm disabled:opacity-60"
                  >
                    {pwSaving ? 'Updating…' : 'Update password'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* C. Buying Session Preferences */}
        <div className="pt-6 border-t border-border-subtle">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-ink-bright uppercase tracking-wider">
              Buying Session Preferences
            </h3>
            <button
              onClick={resetPrefs}
              disabled={prefsSaving}
              className="text-2xs text-ink-muted hover:text-ink-primary disabled:opacity-60"
            >
              <RotateCcw size={11} className="inline mr-1 -mt-px" />
              Reset to defaults
            </button>
          </div>
          <p className="text-xs text-ink-muted mb-4 max-w-2xl">
            Drag rows to reorder. Toggle visibility to hide unused filters or weights from the
            sidebar. Default values pre-fill the input on every new session.
          </p>

          {/* Filters list */}
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <h4 className="text-xs font-medium text-ink-secondary">Filters</h4>
              <span className="text-2xs text-ink-muted">
                Toggle = show in panel. Hidden filters do not apply
              </span>
            </div>
            <div className="border border-border-subtle rounded">
              {[...filterPrefs]
                .sort((a, b) => a.order - b.order)
                .map((pref) => {
                  const def = filterLabelMap.get(pref.key);
                  // Day 95 H.4 — belt-and-suspenders: hidden filters never render
                  // a dead toggle even if a stale pref slipped through.
                  if (!def || def.available === false) return null;
                  return (
                    <div
                      key={pref.key}
                      draggable
                      onDragStart={() => setDragKey(pref.key)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (dragKey && dragKey !== pref.key) reorderFilters(dragKey, pref.key);
                        setDragKey(null);
                      }}
                      onDragEnd={() => setDragKey(null)}
                      className={clsx(
                        'flex items-center gap-3 px-3 py-2 border-b border-border-subtle last:border-b-0',
                        'hover:bg-surface-3 transition-colors',
                        dragKey === pref.key && 'opacity-40'
                      )}
                    >
                      <GripVertical
                        size={14}
                        className="text-ink-muted cursor-grab flex-shrink-0"
                        aria-label="Drag to reorder"
                      />
                      <ArbiToggle
                        label={`Show ${def.label} in panel`}
                        checked={pref.visible}
                        onChange={(v) => setFilterVisible(pref.key, v)}
                      />
                      <span className="text-xs text-ink-primary flex-1 truncate">{def.label}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={pref.default == null ? '' : String(pref.default)}
                        onChange={(e) => setFilterDefault(pref.key, e.target.value)}
                        disabled={!pref.visible}
                        placeholder="—"
                        className={clsx(
                          'arbi-input text-xs font-mono w-24 text-right',
                          !pref.visible && 'opacity-40'
                        )}
                        aria-label={`Default for ${def.label}`}
                      />
                      <span className="text-2xs text-ink-muted w-6 text-right">
                        {def.unit || ''}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Weights list */}
          <div className="mb-4">
            <div className="flex items-baseline justify-between mb-2">
              <h4 className="text-xs font-medium text-ink-secondary">Weights</h4>
              <span className="text-2xs text-ink-muted">
                Toggle = show in panel. To exclude from Buy Score, set the weight to 0
              </span>
            </div>
            <div className="border border-border-subtle rounded">
              {[...weightPrefs]
                .sort((a, b) => a.order - b.order)
                .map((pref) => {
                  const def = weightLabelMap.get(pref.key);
                  if (!def) return null;
                  return (
                    <div
                      key={pref.key}
                      draggable
                      onDragStart={() => setDragKey(pref.key)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (dragKey && dragKey !== pref.key) reorderWeights(dragKey, pref.key);
                        setDragKey(null);
                      }}
                      onDragEnd={() => setDragKey(null)}
                      className={clsx(
                        'flex items-center gap-3 px-3 py-2 border-b border-border-subtle last:border-b-0',
                        'hover:bg-surface-3 transition-colors',
                        dragKey === pref.key && 'opacity-40'
                      )}
                    >
                      <GripVertical
                        size={14}
                        className="text-ink-muted cursor-grab flex-shrink-0"
                        aria-label="Drag to reorder"
                      />
                      <ArbiToggle
                        label={`Show ${def.label} in panel`}
                        checked={pref.visible}
                        onChange={(v) => setWeightVisible(pref.key, v)}
                      />
                      <span className="text-xs text-ink-primary flex-1 truncate">
                        {def.label}
                        {def.penalty && <span className="ml-2 text-2xs text-danger">penalty</span>}
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={pref.default == null ? '' : String(pref.default)}
                        onChange={(e) => setWeightDefault(pref.key, e.target.value)}
                        disabled={!pref.visible}
                        placeholder="—"
                        className={clsx(
                          'arbi-input text-xs font-mono w-24 text-right',
                          !pref.visible && 'opacity-40'
                        )}
                        aria-label={`Default weight for ${def.label}`}
                      />
                    </div>
                  );
                })}
            </div>
          </div>

          <div className="flex justify-end pt-3 border-t border-border-subtle">
            <button
              onClick={savePrefs}
              disabled={prefsSaving}
              className="arbi-btn-primary text-sm disabled:opacity-60"
            >
              <Check size={14} /> {prefsSaving ? 'Saving…' : 'Save preferences'}
            </button>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// ── ORGANIZATION > General ────────────────────────────────────────────────────

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
];

interface SectionWithToastProps {
  sectionRefs: SectionRefs;
  showToast: (t: ToastState) => void;
}

function GeneralSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  const { isAdmin, organization, setOrganization } = useApp();
  const [companyName, setCompanyName] = useState<string>(
    organization?.name ?? 'Ironclad Supply Group'
  );
  const [timezone, setTimezone] = useState<string>(organization?.timezone ?? 'America/New_York');
  const isDirty =
    companyName !== (organization?.name ?? '') || timezone !== (organization?.timezone ?? '');

  // Day 78 P5.3 B.9 — re-sync local state when organization updates externally
  // (e.g., another admin tab writes; setOrganization propagates via AppContext).
  // Intentional dep omission of companyName/timezone — including them would
  // clobber active edits with the external value on every keystroke.
  useEffect(() => {
    if (organization?.name && organization.name !== companyName) {
      setCompanyName(organization.name);
    }
    if (organization?.timezone && organization.timezone !== timezone) {
      setTimezone(organization.timezone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.name, organization?.timezone]);

  // Day 78 P5.3 B.9 — legacy localStorage cleanup (Q2 (a); Audit #2 F12).
  useEffect(() => {
    localStorage.removeItem('arbi-company-name');
    localStorage.removeItem('arbi-timezone');
  }, []);

  async function handleSave(): Promise<void> {
    const { error } = await supabase
      .from('organization')
      .update({
        name: companyName,
        timezone: timezone,
        updated_at: new Date().toISOString(),
      } as never)
      .eq('id', 1);
    if (error) {
      showToast({ kind: 'error', message: `Save failed — ${error.message}` });
      return;
    }
    if (organization) {
      setOrganization({ ...organization, name: companyName, timezone });
    }
    showToast({ kind: 'success', message: 'General settings saved' });
  }

  // ── Branding (logo upload) — admin only ─────────────────────
  // Public-readable arbi-branding bucket. Admin-EXISTS RLS gates writes; the
  // logo URL is persisted on organization.logo_url and read by TopBar.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [logoUploading, setLogoUploading] = useState<boolean>(false);
  const [logoBusy, setLogoBusy] = useState<boolean>(false);

  async function handleLogoFileSelect(file: File): Promise<void> {
    if (file.size > 1024 * 1024) {
      showToast({ kind: 'error', message: 'Logo must be under 1MB' });
      return;
    }
    setLogoUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'png';
      const key = `logo-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('arbi-branding')
        .upload(key, file, { upsert: false, contentType: file.type });
      if (upErr) {
        showToast({
          kind: 'error',
          message:
            'Logo upload failed — file may be too large or connection was interrupted. Try again.',
        });
        return;
      }
      const { data: pub } = supabase.storage.from('arbi-branding').getPublicUrl(key);
      const url = pub.publicUrl;
      const { error: updErr } = await supabase
        .from('organization')
        .update({ logo_url: url, updated_at: new Date().toISOString() } as never)
        .eq('id', 1);
      if (updErr) {
        showToast({ kind: 'error', message: `Save failed — ${updErr.message}` });
        return;
      }
      if (organization) setOrganization({ ...organization, logo_url: url });
      showToast({ kind: 'success', message: 'Logo updated' });
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleLogoRemove(): Promise<void> {
    setLogoBusy(true);
    const { error } = await supabase
      .from('organization')
      .update({ logo_url: null, updated_at: new Date().toISOString() } as never)
      .eq('id', 1);
    setLogoBusy(false);
    if (error) {
      showToast({ kind: 'error', message: `Remove failed — ${error.message}` });
      return;
    }
    if (organization) setOrganization({ ...organization, logo_url: null });
    showToast({ kind: 'success', message: 'Logo removed' });
  }

  return (
    <SectionCard id="general" title="General" sectionRefs={sectionRefs}>
      <div className="space-y-4 max-w-md">
        <div>
          <label
            htmlFor="settings-company-name"
            className="block text-xs font-medium text-ink-secondary mb-1.5"
          >
            Company Name
          </label>
          <input
            id="settings-company-name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            disabled={!isAdmin}
            className="arbi-input w-full text-sm"
          />
        </div>
        <div>
          <label
            htmlFor="settings-timezone"
            className="block text-xs font-medium text-ink-secondary mb-1.5"
          >
            Timezone
          </label>
          <div id="settings-timezone">
            <ArbiSelect
              value={timezone}
              options={TIMEZONE_OPTIONS}
              onChange={(v) => setTimezone(String(v))}
              disabled={!isAdmin}
              className="w-full"
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="settings-currency"
            className="block text-xs font-medium text-ink-secondary mb-1.5"
          >
            Currency
          </label>
          <div id="settings-currency">
            <ArbiSelect
              value="USD"
              options={[{ value: 'USD', label: 'USD — US Dollar' }]}
              onChange={() => {}}
              disabled
              className="w-full"
            />
          </div>
          <p className="text-xs text-ink-muted mt-1">Multi-currency support coming in Phase 2</p>
        </div>
      </div>
      {isAdmin && isDirty && (
        <div className="mt-4 pt-3 border-t border-border-subtle flex justify-end">
          <button onClick={handleSave} className="arbi-btn-primary text-sm">
            <Check size={14} /> Save
          </button>
        </div>
      )}

      {/* Branding — admin only. Public bucket; see migration 1E for the
          deliberate divergence from arbi-photos house style. */}
      {isAdmin && (
        <div className="mt-8 pt-6 border-t border-border-subtle max-w-md">
          <h3 className="text-xs font-semibold text-ink-bright uppercase tracking-wider mb-3">
            Branding
          </h3>
          <p className="text-xs text-ink-muted mb-4">
            Logo renders in the top bar. PNG or JPEG, under 1MB.
          </p>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-md bg-surface-3 flex items-center justify-center flex-shrink-0 overflow-hidden border border-border-subtle">
              {organization?.logo_url ? (
                <img
                  src={organization.logo_url}
                  alt={organization.name ?? 'Logo'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImageIcon size={24} className="text-ink-muted" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                aria-label="Upload organization logo"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleLogoFileSelect(f);
                }}
                className="block text-xs text-ink-secondary file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-surface-3 file:text-ink-primary file:cursor-pointer hover:file:bg-surface-4"
                disabled={logoUploading}
              />
              {organization?.logo_url && (
                <button
                  onClick={handleLogoRemove}
                  disabled={logoBusy}
                  className="text-2xs text-danger hover:text-ink-bright disabled:opacity-60"
                >
                  <Trash2 size={10} className="inline mr-1 -mt-px" />
                  Remove logo
                </button>
              )}
              {logoUploading && <p className="text-2xs text-ink-muted">Uploading…</p>}
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ── BUSINESS RULES > Params & Defaults ────────────────────────────────────────

interface ParamsSectionProps {
  store: UseParamStoreResult;
  sectionRefs: SectionRefs;
  showToast: (t: ToastState) => void;
}

function ParamsSection({ store, sectionRefs, showToast }: ParamsSectionProps): JSX.Element {
  const { distributors } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  // Day 87 P7.3 + D.11 — distributor-mode toolbar state. Generalized shape per
  // #DBN-γ Day 88 forward-compat lock: { mode: ParamLevel; entityId: string | null }
  // accommodates Day 88 CF 64 extension to manufacturer/category/brand/product modes
  // as pure mode-set addition (no refactor). V1 toggle exposes business +
  // distributor only. Ephemeral state per E.8=α (no localStorage, no URL hash —
  // SEED-ONCE clobber class per D.14 feedback memory + multi-operator workstation
  // confusion).
  const [paramsMode, setParamsMode] = useState<{
    mode: ParamLevel;
    entityId: string | null;
  }>({ mode: 'business', entityId: null });

  // Day 88b CF 64 — entity loaders for brand / category / manufacturer modes.
  // Lazy-loaded on first entry into a non-business non-distributor mode
  // (distributors already loaded via useApp() per existing pattern). Stays
  // ParamsSection-local per Sol Phase 0 G5 finding — does NOT expand AppContext
  // since brand/category/manufacturer loaders aren't needed elsewhere in V1.
  // Product mode UI defers to Day 88.5 per Lock #6.
  const [brandsList, setBrandsList] = useState<Array<{ id: string; name: string }>>([]);
  const [categoriesList, setCategoriesList] = useState<Array<{ id: string; name: string }>>([]);
  const [manufacturersList, setManufacturersList] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [entitiesLoaded, setEntitiesLoaded] = useState<boolean>(false);
  const [entitiesLoading, setEntitiesLoading] = useState<boolean>(false);

  useEffect(() => {
    // Trigger lazy-load only when entering a non-business non-distributor mode.
    // Day 88.5 P3 — product mode is also skipped here (ProductPickerTypeahead
    // loads products independently via server-side search; brands/categories/
    // manufacturers bulk-loads aren't needed for product mode).
    if (
      paramsMode.mode === 'business' ||
      paramsMode.mode === 'distributor' ||
      paramsMode.mode === 'product'
    )
      return;
    if (entitiesLoaded || entitiesLoading) return;
    let cancelled = false;
    setEntitiesLoading(true);
    (async () => {
      const [b, c, m] = await Promise.all([
        supabase.from('brands').select('id, name').order('name'),
        supabase.from('categories').select('id, name').order('name'),
        supabase.from('manufacturers').select('id, name').order('name'),
      ]);
      if (cancelled) return;
      setBrandsList((b.data ?? []) as Array<{ id: string; name: string }>);
      setCategoriesList((c.data ?? []) as Array<{ id: string; name: string }>);
      setManufacturersList((m.data ?? []) as Array<{ id: string; name: string }>);
      setEntitiesLoaded(true);
      setEntitiesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [paramsMode.mode, entitiesLoaded, entitiesLoading]);

  // Day 88.5 P3 — URL deep-link consumer. Reads ?mode={paramLevel}&entity_id={id}
  // from location.search, validates against PARAM_LEVELS + non-empty entity_id,
  // pre-sets paramsMode if valid, then strips search params via replace-history
  // navigate to prevent re-fire on remount (preserves hash for section anchor).
  //
  // Triggered by ProductDetail's "Override params for this product" button at
  // src/pages/catalog/ProductDetail.tsx (Day 88.5 P3 Q4 site (c) page-header).
  //
  // Graceful degradation: invalid mode or entity_id → no-op. Invalid product
  // entity_id (well-formed but no row) → ProductPickerTypeahead's self-contained
  // label fetch returns null → picker shows "Search products..." placeholder.
  //
  // Convention 38 informational (Day 88.5 P3 close): URL syntax uses standard
  // query-before-hash form (`/settings?mode=product&entity_id={id}#section-params`)
  // for `location.search` consumption per WHATWG URL spec; Sol Phase 0 L5
  // lean cited non-standard hash-before-query form which would have placed
  // params inside the hash fragment.
  useEffect(() => {
    if (!location.search) return;
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const entityId = params.get('entity_id');
    if (mode && PARAM_LEVELS.includes(mode as ParamLevel) && entityId && entityId.length > 0) {
      setParamsMode({ mode: mode as ParamLevel, entityId });
      // Strip consumed params; preserve hash for section anchor.
      navigate({ pathname: location.pathname, hash: location.hash }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- single-shot consumer
  }, [location.search]);

  const businessParams: ParamRow[] = store.getByLevel('business');
  const byKey: Record<string, ParamRow | undefined> = Object.fromEntries(
    businessParams.map((p) => [p.key, p])
  );

  // Params not in any defined group (business mode only; E.2=iii — Other Parameters
  // absent in distributor mode).
  const groupedKeys = PARAM_GROUPS_SETTINGS.flatMap((g) => g.keys);
  const ungrouped = businessParams.filter((p) => !groupedKeys.includes(p.key));

  async function handleSave(
    id: string,
    newValue: number | string | null,
    reason: string
  ): Promise<void> {
    await store.updateParam(id, newValue, reason);
    showToast({ kind: 'success', message: 'Parameter updated' });
  }

  if (store.loading) {
    return (
      <SectionCard id="params" title="Params & Defaults" sectionRefs={sectionRefs}>
        <div className="py-8 text-center text-sm text-ink-muted">Loading parameters...</div>
      </SectionCard>
    );
  }

  // Day 88b CF 64 — generalize from distributor-only checks to mode-aware.
  // `inEntityMode` covers ALL non-business modes (distributor / manufacturer /
  // category / brand); `entitySelected` true when entity_id picked.
  const inEntityMode = paramsMode.mode !== 'business';
  const entitySelected = inEntityMode && paramsMode.entityId !== null;

  // Mode-specific entity picker options. Distributors come from useApp (existing
  // pattern); brands/categories/manufacturers come from lazy-loaded ParamsSection state.
  // Product mode UI defers Day 88.5 — picker returns empty array (no options) to
  // signal not-yet-implemented state if accidentally triggered.
  function getEntityPickerOptions(): Array<{ value: string; label: string }> {
    switch (paramsMode.mode) {
      case 'distributor':
        return distributors.map((d) => ({ value: d.id, label: `${d.name} (${d.code})` }));
      case 'manufacturer':
        return manufacturersList.map((m) => ({ value: m.id, label: m.name }));
      case 'category':
        return categoriesList.map((c) => ({ value: c.id, label: c.name }));
      case 'brand':
        return brandsList.map((b) => ({ value: b.id, label: b.name }));
      case 'product':
        // Day 88.5 P2 — product mode renders ProductPickerTypeahead at the
        // toolbar slot via conditional render below; this switch arm is
        // unreachable post-P2 conditional render. Retained for switch
        // exhaustiveness + hygiene against future refactor regression.
        return [];
      default:
        return [];
    }
  }

  // Mode label for placeholder + empty state copy ("Select a manufacturer...", etc.)
  function getModeLabel(): string {
    switch (paramsMode.mode) {
      case 'distributor':
        return 'distributor';
      case 'manufacturer':
        return 'manufacturer';
      case 'category':
        return 'category';
      case 'brand':
        return 'brand';
      case 'product':
        return 'product';
      default:
        return 'entity';
    }
  }

  // Day 88b CF 64 (supersedes Day 87 P7.3 + A2=c) — filter groups by active
  // entity mode support via per-key supportedLevels (group default + KEYS_META
  // per-key overrides). In business mode all groups visible; in non-business
  // modes only groups with at least one key supporting the active level show.
  // Semantic changes vs Day 87:
  //  - Engine Tuning: now VISIBLE in distributor mode (was hidden) since Sol §A1
  //    promotes default_lead_time_days + invoice_variance_tolerance_pct to
  //    intermediate scope ['business','distributor','manufacturer'].
  //  - Credit Thresholds + Fulfillment Costs: now HIDDEN in distributor mode
  //    (were accidentally visible with no operational meaning since their keys
  //    are business-only / engine-unread; new behavior correctly hides them).
  const visibleGroups =
    paramsMode.mode === 'business'
      ? PARAM_GROUPS_SETTINGS
      : PARAM_GROUPS_SETTINGS.filter((g) => groupSupportsLevel(g, paramsMode.mode));

  return (
    <div
      id="section-params"
      ref={(el) => {
        if (sectionRefs) sectionRefs.current['params'] = el;
      }}
      className="space-y-4"
    >
      {/* Day 88b CF 64 — entity-mode toolbar (supersedes Day 87 P7.3 + D.11
          distributor-only 2-button toolbar). 6 buttons (Business / Distributor /
          Manufacturer / Category / Brand / Product); product picker typeahead ships
          Day 88.5 P2 for 1955+ row scaling per Sol Lock #6 fulfillment. */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-surface-2 rounded-lg">
        <span className="text-xs text-ink-muted">View / Edit:</span>
        <div className="flex gap-1">
          <button
            onClick={() => setParamsMode({ mode: 'business', entityId: null })}
            className={
              paramsMode.mode === 'business'
                ? 'arbi-btn-primary text-xs px-2.5 py-1'
                : 'arbi-btn-ghost text-xs px-2.5 py-1'
            }
          >
            Business defaults
          </button>
          <button
            onClick={() => setParamsMode({ mode: 'distributor', entityId: null })}
            className={
              paramsMode.mode === 'distributor'
                ? 'arbi-btn-primary text-xs px-2.5 py-1'
                : 'arbi-btn-ghost text-xs px-2.5 py-1'
            }
          >
            Distributor
          </button>
          <button
            onClick={() => setParamsMode({ mode: 'manufacturer', entityId: null })}
            className={
              paramsMode.mode === 'manufacturer'
                ? 'arbi-btn-primary text-xs px-2.5 py-1'
                : 'arbi-btn-ghost text-xs px-2.5 py-1'
            }
          >
            Manufacturer
          </button>
          <button
            onClick={() => setParamsMode({ mode: 'category', entityId: null })}
            className={
              paramsMode.mode === 'category'
                ? 'arbi-btn-primary text-xs px-2.5 py-1'
                : 'arbi-btn-ghost text-xs px-2.5 py-1'
            }
          >
            Category
          </button>
          <button
            onClick={() => setParamsMode({ mode: 'brand', entityId: null })}
            className={
              paramsMode.mode === 'brand'
                ? 'arbi-btn-primary text-xs px-2.5 py-1'
                : 'arbi-btn-ghost text-xs px-2.5 py-1'
            }
          >
            Brand
          </button>
          <button
            onClick={() => setParamsMode({ mode: 'product', entityId: null })}
            className={
              paramsMode.mode === 'product'
                ? 'arbi-btn-primary text-xs px-2.5 py-1'
                : 'arbi-btn-ghost text-xs px-2.5 py-1'
            }
          >
            Product
          </button>
        </div>
        {inEntityMode && (
          <div className="w-72 ml-2">
            {paramsMode.mode === 'product' ? (
              /* Day 88.5 P2 — product mode renders typeahead instead of
                 ArbiSelect (1955+ row scaling per Sol Lock #6). Both writers
                 update paramsMode.entityId via the same setter shape. */
              <ProductPickerTypeahead
                value={paramsMode.entityId}
                onChange={(id) => setParamsMode({ mode: 'product', entityId: id })}
                placeholder="Search products..."
              />
            ) : (
              <ArbiSelect
                value={paramsMode.entityId ?? ''}
                onChange={(v) =>
                  setParamsMode({ mode: paramsMode.mode, entityId: String(v) || null })
                }
                options={[
                  { value: '', label: `Select a ${getModeLabel()}...` },
                  ...getEntityPickerOptions(),
                ]}
              />
            )}
          </div>
        )}
        {inEntityMode && entitiesLoading && paramsMode.mode !== 'distributor' && (
          <span className="text-xs text-ink-muted">Loading {getModeLabel()}s...</span>
        )}
      </div>

      {/* Day 88b Phase 3 — Settings caption per ADR 42 + Decision 35 operator-
          controlled-recalc reminder. Visible in any non-business mode (whether
          entity selected or not — operator sees this immediately on entering
          override mode so the recalc step is set up before edits land). */}
      {inEntityMode && (
        <div className="px-4 py-2 bg-warning/10 border border-warning/30 rounded-md text-xs text-ink-secondary">
          Saving overrides affects cached profitability for downstream products. Run{' '}
          <span className="font-semibold">Recalculate</span> in Buying Session to refresh.
        </div>
      )}

      {/* Empty state — entity mode active, no entity selected (generalized E.7=i) */}
      {inEntityMode && !entitySelected && (
        <div className="bg-surface-1 border border-border rounded-lg p-8 text-center">
          <SlidersHorizontal size={48} className="text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-muted">
            Select a {getModeLabel()} to view or edit overrides
          </p>
        </div>
      )}

      {/* Render groups — business mode OR entity mode + selected */}
      {(!inEntityMode || entitySelected) &&
        visibleGroups.map((group) => {
          if (inEntityMode) {
            // Day 88b entity mode: render InlineParamOverrideRow per group key.
            // Filter by per-key effective supportedLevels (per-group default +
            // KEYS_META overrides) — only show keys that operationally support
            // the active mode (e.g., staleness keys hidden in manufacturer mode
            // since they're business-only per KEYS_META).
            const keys = group.keys.filter(
              (k) => byKey[k] && effectiveSupportedLevels(group, k).includes(paramsMode.mode)
            );
            if (!keys.length) return null;
            const entityLevel = paramsMode.mode as
              | 'distributor'
              | 'manufacturer'
              | 'category'
              | 'brand';
            return (
              <div key={group.label} className="bg-surface-1 border border-border rounded-lg">
                <div className="px-4 py-3 border-b border-border-subtle">
                  <h2 className="text-sm font-semibold text-ink-bright">{group.label}</h2>
                  {group.caption && <p className="text-xs text-ink-muted mt-1">{group.caption}</p>}
                </div>
                <div className="px-3 py-1 divide-y divide-border-subtle">
                  {keys.map((k) => (
                    <InlineParamOverrideRow
                      key={k}
                      paramKey={k}
                      store={store}
                      entityLevel={entityLevel}
                      entityId={paramsMode.entityId}
                      onSaved={() => showToast({ kind: 'success', message: 'Override saved' })}
                      onError={(msg) => showToast({ kind: 'error', message: msg })}
                    />
                  ))}
                </div>
              </div>
            );
          }

          // Business mode — existing InlineParamRow render.
          const rows: ParamRow[] = group.keys
            .map((k) => byKey[k])
            .filter((p): p is ParamRow => !!p);
          if (!rows.length) return null;
          return (
            <div key={group.label} className="bg-surface-1 border border-border rounded-lg">
              <div className="px-4 py-3 border-b border-border-subtle">
                <h2 className="text-sm font-semibold text-ink-bright">{group.label}</h2>
                {/* Day 86 P7.2 — optional subsection-level guidance per TONE_STANDARDS §9 */}
                {group.caption && <p className="text-xs text-ink-muted mt-1">{group.caption}</p>}
              </div>
              <div className="px-3 py-1 divide-y divide-border-subtle">
                {rows.map((p) => (
                  <InlineParamRow key={p.id} param={p} onSave={handleSave} />
                ))}
              </div>
            </div>
          );
        })}

      {/* Other Parameters — business mode only per E.2=iii */}
      {!inEntityMode && ungrouped.length > 0 && (
        <div className="bg-surface-1 border border-border rounded-lg">
          <div className="px-4 py-3 border-b border-border-subtle">
            <h2 className="text-sm font-semibold text-ink-bright">Other Parameters</h2>
          </div>
          <div className="px-3 py-1 divide-y divide-border-subtle">
            {ungrouped.map((p) => (
              <InlineParamRow key={p.id} param={p} onSave={handleSave} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state — business mode with no params loaded */}
      {!inEntityMode && businessParams.length === 0 && (
        <div className="bg-surface-1 border border-border rounded-lg p-8 text-center">
          <SlidersHorizontal size={48} className="text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-muted">No business parameters found</p>
        </div>
      )}
    </div>
  );
}

// ── BUSINESS RULES > Channels & Paths ────────────────────────────────────────
// Day 86 P7.2 + A1 + A7 — relocates marketplace + fulfillment-path is_active
// toggles from arbi-dev FormulaEditor (Day 63a graduation; FormulaEditor
// Sections 1+2 emptied per A7 placeholder card) to arbi-root Settings per
// CF 41 cleavage + ADR Decision 39. Closes Audit #2 Finding 6 dual-write
// surface (paired with Directory.MarketplaceForm 6-touchpoint slim per A1).
// Admin-only nav per ADMIN_ONLY_SETTINGS (Architecture Rule 4 — channel +
// path activation is engine-relevant policy). Renders 2 sub-sections:
// channel availability + path availability + read-only metadata (incl.
// new path_type column added at Day 86 migration §1-§3). ConfirmDialog
// gate on deactivation only per Variant A copy (Round 2 §R5).

interface ChannelsAndPathsSectionProps {
  sectionRefs: SectionRefs;
  showToast: (t: ToastState) => void;
}

interface ChannelsMarketplaceRow {
  id: string;
  channel: string;
  display_name: string;
  is_active: boolean;
}

interface ChannelsPathRow {
  id: string;
  path_key: string;
  display_name: string;
  is_active: boolean;
  sort_order: number;
  applies_inbound_shipping: boolean;
  applies_prep_fee: boolean;
  payout_param_key: string;
  path_type: 'merchant_managed' | 'fulfillment_service_managed' | 'dropship';
}

const PATH_TYPE_LABELS: Record<string, string> = {
  merchant_managed: 'Merchant-managed',
  fulfillment_service_managed: 'Fulfillment service',
  dropship: 'Dropship',
};

function ChannelsAndPathsSection({
  sectionRefs,
  showToast,
}: ChannelsAndPathsSectionProps): JSX.Element {
  const [marketplaces, setMarketplaces] = useState<ChannelsMarketplaceRow[]>([]);
  const [paths, setPaths] = useState<ChannelsPathRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmToggle, setConfirmToggle] = useState<
    | { kind: 'marketplace'; row: ChannelsMarketplaceRow }
    | { kind: 'path'; row: ChannelsPathRow }
    | null
  >(null);

  const loadMarketplaces = useCallback(async () => {
    const { data, error } = await supabase
      .from('marketplaces')
      .select('id, channel, display_name, is_active')
      .order('display_name', { ascending: true });
    if (error) {
      showToast({ kind: 'error', message: `Marketplaces load failed — ${error.message}` });
      return;
    }
    setMarketplaces((data ?? []) as unknown as ChannelsMarketplaceRow[]);
  }, [showToast]);

  const loadPaths = useCallback(async () => {
    const { data, error } = await supabase
      .from('fulfillment_path_config')
      .select(
        'id, path_key, display_name, is_active, sort_order, applies_inbound_shipping, applies_prep_fee, payout_param_key, path_type'
      )
      .order('sort_order', { ascending: true });
    if (error) {
      showToast({ kind: 'error', message: `Paths load failed — ${error.message}` });
      return;
    }
    setPaths((data ?? []) as unknown as ChannelsPathRow[]);
  }, [showToast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.all([loadMarketplaces(), loadPaths()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadMarketplaces, loadPaths]);

  async function toggleMarketplaceConfirmed(row: ChannelsMarketplaceRow): Promise<void> {
    const { error } = await supabase
      .from('marketplaces')
      .update({ is_active: !row.is_active } as never)
      .eq('id', row.id);
    if (error) {
      showToast({ kind: 'error', message: `Marketplace toggle failed — ${error.message}` });
      return;
    }
    showToast({
      kind: 'success',
      message: `${row.display_name} ${!row.is_active ? 'activated' : 'deactivated'}`,
    });
    await loadMarketplaces();
  }

  async function togglePathConfirmed(row: ChannelsPathRow): Promise<void> {
    const { error } = await supabase
      .from('fulfillment_path_config')
      .update({ is_active: !row.is_active, updated_at: new Date().toISOString() } as never)
      .eq('id', row.id);
    if (error) {
      showToast({ kind: 'error', message: `Path toggle failed — ${error.message}` });
      return;
    }
    showToast({
      kind: 'success',
      message: `${row.display_name} ${!row.is_active ? 'activated' : 'deactivated'}`,
    });
    await loadPaths();
  }

  function requestToggleMarketplace(row: ChannelsMarketplaceRow): void {
    if (row.is_active) setConfirmToggle({ kind: 'marketplace', row });
    else void toggleMarketplaceConfirmed(row);
  }

  function requestTogglePath(row: ChannelsPathRow): void {
    if (row.is_active) setConfirmToggle({ kind: 'path', row });
    else void togglePathConfirmed(row);
  }

  return (
    <div
      id="section-channels-and-paths"
      ref={(el) => {
        if (sectionRefs) sectionRefs.current['channels-and-paths'] = el;
      }}
      className="space-y-4"
    >
      {/* Channel availability sub-section */}
      <div className="bg-surface-1 border border-border rounded-lg">
        <div className="px-4 py-3 border-b border-border-subtle">
          <h2 className="text-sm font-semibold text-ink-bright">Channel availability</h2>
          <p className="text-xs text-ink-muted mt-1">
            Toggling a channel off suppresses it from new buying sessions. Existing rows on
            historical entities remain unchanged. Directory → Marketplaces edits identity, fees, and
            connection metadata; this section owns <code>is_active</code> only.
          </p>
        </div>
        <div className="px-3 py-1 divide-y divide-border-subtle">
          {loading ? (
            <div className="py-6 text-center text-sm text-ink-muted">Loading…</div>
          ) : marketplaces.length === 0 ? (
            <div className="py-6 text-center text-sm text-ink-muted">
              No marketplaces configured
            </div>
          ) : (
            marketplaces.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-2.5 px-1">
                <div>
                  <span className="text-sm text-ink-primary">{m.display_name}</span>
                  <span className="text-2xs text-ink-muted ml-2 font-mono">{m.channel}</span>
                </div>
                <ArbiToggle
                  checked={m.is_active}
                  onChange={() => requestToggleMarketplace(m)}
                  label={`Toggle ${m.display_name}`}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Path availability + read-only metadata sub-section */}
      <div className="bg-surface-1 border border-border rounded-lg">
        <div className="px-4 py-3 border-b border-border-subtle">
          <h2 className="text-sm font-semibold text-ink-bright">
            Path availability and engine metadata
          </h2>
          <p className="text-xs text-ink-muted mt-1">
            Toggling a path off retires it from profitability calculations on the next Recalculate.
            Cached rows for the path are removed at that time. Engine metadata fields are read-only
            in V1 per ADR Decision 34.
          </p>
        </div>
        <div className="px-3 py-1 divide-y divide-border-subtle">
          {loading ? (
            <div className="py-6 text-center text-sm text-ink-muted">Loading…</div>
          ) : paths.length === 0 ? (
            <div className="py-6 text-center text-sm text-ink-muted">No paths configured</div>
          ) : (
            paths.map((p) => (
              <ChannelsPathRowDisplay key={p.id} path={p} onToggle={() => requestTogglePath(p)} />
            ))
          )}
        </div>
      </div>

      {/* ConfirmDialog gate on deactivation only — Variant A copy per Round 2 §R5 */}
      <ConfirmDialog
        open={confirmToggle !== null}
        title={confirmToggle ? `Deactivate ${confirmToggle.row.display_name}` : ''}
        message={
          confirmToggle?.kind === 'marketplace'
            ? `Hides ${confirmToggle.row.display_name} from new buying sessions. Historical rows are unchanged. Reactivate from this section anytime.`
            : confirmToggle?.kind === 'path'
              ? `Retires ${confirmToggle.row.display_name} from profitability calculations after the next Recalculate. Historical rows are unchanged. Reactivate from this section anytime.`
              : ''
        }
        confirmLabel={confirmToggle ? `Deactivate ${confirmToggle.row.display_name}` : 'Confirm'}
        onConfirm={() => {
          const cur = confirmToggle;
          setConfirmToggle(null);
          if (!cur) return;
          if (cur.kind === 'marketplace') void toggleMarketplaceConfirmed(cur.row);
          else void togglePathConfirmed(cur.row);
        }}
        onCancel={() => setConfirmToggle(null)}
      />
    </div>
  );
}

interface ChannelsPathRowDisplayProps {
  path: ChannelsPathRow;
  onToggle: () => void;
}

function ChannelsPathRowDisplay({ path, onToggle }: ChannelsPathRowDisplayProps): JSX.Element {
  return (
    <div className="py-2.5 px-1">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm text-ink-primary">{path.display_name}</span>
          <span className="text-2xs text-ink-muted ml-2 font-mono">{path.path_key}</span>
        </div>
        <ArbiToggle
          checked={path.is_active}
          onChange={onToggle}
          label={`Toggle ${path.display_name}`}
        />
      </div>
      <div className="mt-2 grid grid-cols-4 gap-3 text-2xs">
        <ChannelsMetadataField
          label="Type"
          value={PATH_TYPE_LABELS[path.path_type] || path.path_type}
        />
        <ChannelsMetadataFlag label="Inbound shipping" value={path.applies_inbound_shipping} />
        <ChannelsMetadataFlag label="Prep fee" value={path.applies_prep_fee} />
        <ChannelsMetadataField label="Payout param" value={path.payout_param_key} />
      </div>
    </div>
  );
}

interface ChannelsMetadataFlagProps {
  label: string;
  value: boolean;
}

function ChannelsMetadataFlag({ label, value }: ChannelsMetadataFlagProps): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span className={value ? 'text-success' : 'text-ink-muted'}>{value ? '✓' : '—'}</span>
      <span className="text-ink-muted">{label}</span>
    </div>
  );
}

interface ChannelsMetadataFieldProps {
  label: string;
  value: string;
}

function ChannelsMetadataField({ label, value }: ChannelsMetadataFieldProps): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span className="text-ink-muted">{label}:</span>
      <code className="text-ink-secondary text-2xs truncate">{value || '—'}</code>
    </div>
  );
}

// ── BUSINESS RULES > Cost Line Items (Day 87 P7.3 + D.8) ─────────────────────
// Surfaces the cost_line_items table (Day 66 P2.1 substrate) for operator
// management. 5 base rows in business mode (supplier_shipping_per_unit /
// inbound_shipping_per_unit / misc_fee_per_unit / fba_prep_fee_default /
// fbm_shipping_per_unit). Per-section distributor-mode toolbar per A5=b
// (independent from ParamsSection toolbar state). Edit + Toggle only per
// A3=a (no Add / no Delete on base rows; calculation_type read-only badge
// per A4=a; engine support gates editability — Day 68 Q3 lock at BACKLOG
// L517). admin-gated per Architecture Rule 4.

type CostCalculationType = 'per_unit_usd' | 'pct_of_cost' | 'flat_usd';

interface CostLineItemRow {
  id: string;
  key: string;
  display_name: string;
  calculation_type: CostCalculationType;
  value: number;
  applies_to_paths: string[];
  distributor_id: string | null;
  is_active: boolean;
  notes: string | null;
}

const CALC_TYPE_LABELS: Record<CostCalculationType, string> = {
  per_unit_usd: 'Per Unit USD',
  pct_of_cost: '% of Cost',
  flat_usd: 'Flat USD',
};

function CostLineItemsSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  const { distributors } = useApp();
  const { activePaths } = useSettingsConfig();
  const [rows, setRows] = useState<CostLineItemRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Day 87 P7.3 + A5=b — per-section distributor-mode toolbar state. Independent
  // from ParamsSection's paramsMode (separate useState in separate component).
  // Generalized shape per #DBN-γ Day 88 forward-compat lock.
  const [cliMode, setCliMode] = useState<{
    mode: ParamLevel;
    entityId: string | null;
  }>({ mode: 'business', entityId: null });

  const [editingValueId, setEditingValueId] = useState<string | null>(null);
  const [valueDraft, setValueDraft] = useState<string>('');
  const [valueErr, setValueErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [confirmRemove, setConfirmRemove] = useState<CostLineItemRow | null>(null);
  const valueInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editingValueId && valueInputRef.current) valueInputRef.current.focus();
  }, [editingValueId]);

  const loadRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await supabase
      .from('cost_line_items')
      .select(
        'id, key, display_name, calculation_type, value, applies_to_paths, distributor_id, is_active, notes'
      )
      .order('key', { ascending: true });
    if (error) {
      showToast({ kind: 'error', message: `Cost line items load failed — ${error.message}` });
      setLoading(false);
      return;
    }
    setRows((data ?? []) as unknown as CostLineItemRow[]);
    setLoading(false);
  }, [showToast]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const inDistributorMode = cliMode.mode === 'distributor';
  const distributorSelected = inDistributorMode && cliMode.entityId !== null;

  // Base rows (distributor_id NULL), sorted by key.
  const baseRows = rows.filter((r) => r.distributor_id === null);

  // Find override row for a given base key + selected distributor.
  function findOverride(baseKey: string): CostLineItemRow | undefined {
    if (!cliMode.entityId) return undefined;
    return rows.find((r) => r.key === baseKey && r.distributor_id === cliMode.entityId);
  }

  // Start editing value (business or distributor mode). Per-row state.
  function startEditValue(row: CostLineItemRow, displayValue: number): void {
    setEditingValueId(row.id);
    setValueDraft(String(displayValue));
    setValueErr(null);
  }

  function cancelEditValue(): void {
    setEditingValueId(null);
    setValueDraft('');
    setValueErr(null);
  }

  // Save value edit. In business mode: UPDATE the base row directly.
  // In distributor mode: INSERT (if override absent) or UPDATE (if exists) — empty draft on existing override = clear/Remove.
  async function saveValue(baseRow: CostLineItemRow): Promise<void> {
    const trimmed = valueDraft.trim();
    setBusy(true);
    setValueErr(null);
    try {
      if (inDistributorMode) {
        if (!cliMode.entityId) {
          setValueErr('No distributor selected');
          setBusy(false);
          return;
        }
        const override = findOverride(baseRow.key);
        if (!trimmed) {
          // Empty draft = clear override
          if (override) {
            const { error } = await supabase.from('cost_line_items').delete().eq('id', override.id);
            if (error) throw error;
            showToast({ kind: 'success', message: `${baseRow.display_name} override removed` });
          }
          cancelEditValue();
          await loadRows();
          setBusy(false);
          return;
        }
        const parsed = parseFloat(trimmed);
        if (!Number.isFinite(parsed)) {
          setValueErr('Invalid number');
          setBusy(false);
          return;
        }
        if (override) {
          const { error } = await supabase
            .from('cost_line_items')
            .update({ value: parsed } as never)
            .eq('id', override.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('cost_line_items').insert({
            key: baseRow.key,
            display_name: baseRow.display_name,
            calculation_type: baseRow.calculation_type,
            value: parsed,
            applies_to_paths: baseRow.applies_to_paths,
            distributor_id: cliMode.entityId,
            is_active: baseRow.is_active,
          } as never);
          if (error) throw error;
        }
        showToast({ kind: 'success', message: `${baseRow.display_name} override saved` });
      } else {
        // Business mode — UPDATE base row.
        const parsed = parseFloat(trimmed);
        if (!Number.isFinite(parsed)) {
          setValueErr('Invalid number');
          setBusy(false);
          return;
        }
        const { error } = await supabase
          .from('cost_line_items')
          .update({ value: parsed } as never)
          .eq('id', baseRow.id);
        if (error) throw error;
        showToast({ kind: 'success', message: `${baseRow.display_name} updated` });
      }
      cancelEditValue();
      await loadRows();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setValueErr(msg);
      showToast({ kind: 'error', message: msg });
    }
    setBusy(false);
  }

  // Toggle applies_to_paths membership. In business mode: UPDATE base row.
  // In distributor mode: INSERT (or UPDATE) override row with the new applies_to_paths.
  async function toggleAppliesToPath(baseRow: CostLineItemRow, pathKey: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const targetRow = inDistributorMode ? (findOverride(baseRow.key) ?? baseRow) : baseRow;
      const current = targetRow.applies_to_paths;
      const next = current.includes(pathKey)
        ? current.filter((k) => k !== pathKey)
        : [...current, pathKey];
      if (inDistributorMode) {
        if (!cliMode.entityId) {
          setBusy(false);
          return;
        }
        const override = findOverride(baseRow.key);
        if (override) {
          const { error } = await supabase
            .from('cost_line_items')
            .update({ applies_to_paths: next } as never)
            .eq('id', override.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('cost_line_items').insert({
            key: baseRow.key,
            display_name: baseRow.display_name,
            calculation_type: baseRow.calculation_type,
            value: baseRow.value,
            applies_to_paths: next,
            distributor_id: cliMode.entityId,
            is_active: baseRow.is_active,
          } as never);
          if (error) throw error;
        }
      } else {
        const { error } = await supabase
          .from('cost_line_items')
          .update({ applies_to_paths: next } as never)
          .eq('id', baseRow.id);
        if (error) throw error;
      }
      await loadRows();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      showToast({ kind: 'error', message: msg });
    }
    setBusy(false);
  }

  // Toggle is_active. Same INSERT-or-UPDATE pattern as applies_to_paths.
  async function toggleActive(baseRow: CostLineItemRow): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const targetRow = inDistributorMode ? (findOverride(baseRow.key) ?? baseRow) : baseRow;
      const next = !targetRow.is_active;
      if (inDistributorMode) {
        if (!cliMode.entityId) {
          setBusy(false);
          return;
        }
        const override = findOverride(baseRow.key);
        if (override) {
          const { error } = await supabase
            .from('cost_line_items')
            .update({ is_active: next } as never)
            .eq('id', override.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('cost_line_items').insert({
            key: baseRow.key,
            display_name: baseRow.display_name,
            calculation_type: baseRow.calculation_type,
            value: baseRow.value,
            applies_to_paths: baseRow.applies_to_paths,
            distributor_id: cliMode.entityId,
            is_active: next,
          } as never);
          if (error) throw error;
        }
      } else {
        const { error } = await supabase
          .from('cost_line_items')
          .update({ is_active: next } as never)
          .eq('id', baseRow.id);
        if (error) throw error;
      }
      await loadRows();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      showToast({ kind: 'error', message: msg });
    }
    setBusy(false);
  }

  // Remove distributor override per ConfirmDialog gate.
  async function removeOverride(): Promise<void> {
    if (!confirmRemove) return;
    const override = findOverride(confirmRemove.key);
    if (!override) {
      setConfirmRemove(null);
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.from('cost_line_items').delete().eq('id', override.id);
      if (error) throw error;
      showToast({
        kind: 'success',
        message: `${confirmRemove.display_name} override removed`,
      });
      setConfirmRemove(null);
      await loadRows();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      showToast({ kind: 'error', message: msg });
    }
    setBusy(false);
  }

  function handleValueKeyDown(
    e: ReactKeyboardEvent<HTMLInputElement>,
    baseRow: CostLineItemRow
  ): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      void saveValue(baseRow);
    }
    if (e.key === 'Escape') cancelEditValue();
  }

  return (
    <SectionCard id="cost-line-items" title="Cost Line Items" sectionRefs={sectionRefs}>
      {/* Day 87 P7.3 + D.11 + A5=b — per-section distributor-mode toolbar */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-surface-2 rounded-lg mb-4">
        <span className="text-xs text-ink-muted">View / Edit:</span>
        <div className="flex gap-1">
          <button
            onClick={() => setCliMode({ mode: 'business', entityId: null })}
            className={
              cliMode.mode === 'business'
                ? 'arbi-btn-primary text-xs px-2.5 py-1'
                : 'arbi-btn-ghost text-xs px-2.5 py-1'
            }
          >
            Business defaults
          </button>
          <button
            onClick={() => setCliMode({ mode: 'distributor', entityId: null })}
            className={
              cliMode.mode === 'distributor'
                ? 'arbi-btn-primary text-xs px-2.5 py-1'
                : 'arbi-btn-ghost text-xs px-2.5 py-1'
            }
          >
            Distributor overrides
          </button>
        </div>
        {inDistributorMode && (
          <div className="w-72 ml-2">
            <ArbiSelect
              value={cliMode.entityId ?? ''}
              onChange={(v) => setCliMode({ mode: 'distributor', entityId: String(v) || null })}
              options={[
                { value: '', label: 'Select a distributor...' },
                ...distributors.map((d) => ({ value: d.id, label: `${d.name} (${d.code})` })),
              ]}
            />
          </div>
        )}
      </div>

      {loading && (
        <div className="py-8 text-center text-sm text-ink-muted">Loading cost line items...</div>
      )}

      {/* Empty state — distributor mode active, no distributor selected (E.7=i) */}
      {!loading && inDistributorMode && !distributorSelected && (
        <div className="bg-surface-2 rounded-lg p-8 text-center">
          <SlidersHorizontal size={48} className="text-ink-muted mx-auto mb-3" />
          <p className="text-sm text-ink-muted">Select a distributor to view or edit overrides</p>
        </div>
      )}

      {/* Row list — business mode or distributor mode + selected */}
      {!loading && (!inDistributorMode || distributorSelected) && (
        <div className="space-y-2">
          {baseRows.map((baseRow) => {
            const override = inDistributorMode ? findOverride(baseRow.key) : undefined;
            const effectiveRow = override ?? baseRow;
            const isEditingValue = editingValueId === baseRow.id;
            const valueDisplay = effectiveRow.value.toFixed(2);
            const baseValueDisplay = baseRow.value.toFixed(2);

            return (
              <div
                key={baseRow.id}
                className="flex items-center gap-3 py-2.5 px-3 bg-surface-2 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-ink-primary">
                    {baseRow.display_name}
                  </span>
                  <p className="text-2xs text-ink-muted font-mono mt-0.5">{baseRow.key}</p>
                </div>

                {/* A4=a — calculation_type read-only badge */}
                <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-info/15 text-info">
                  {CALC_TYPE_LABELS[baseRow.calculation_type]}
                </span>

                {/* Value — click-to-edit (base or override depending on mode) */}
                <div className="w-44 text-right">
                  {inDistributorMode && (
                    <div className="text-2xs text-ink-muted mb-0.5">
                      Base: <span className="font-mono">${baseValueDisplay}</span>
                    </div>
                  )}
                  {isEditingValue ? (
                    <div className="space-y-1.5">
                      <input
                        ref={valueInputRef}
                        type="text"
                        value={valueDraft}
                        onChange={(e) => setValueDraft(e.target.value)}
                        onKeyDown={(e) => handleValueKeyDown(e, baseRow)}
                        className="arbi-input w-full text-sm text-right"
                        placeholder={inDistributorMode && override ? '(empty = clear)' : '0.00'}
                      />
                      {valueErr && <p className="text-xs text-danger text-right">{valueErr}</p>}
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => void saveValue(baseRow)}
                          disabled={busy}
                          className="arbi-btn-primary text-xs px-2 py-1"
                        >
                          <Check size={11} /> Save
                        </button>
                        <button
                          onClick={cancelEditValue}
                          className="arbi-btn-ghost text-xs px-2 py-1"
                          aria-label="Cancel"
                        >
                          <X size={11} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() =>
                          startEditValue(baseRow, override ? override.value : baseRow.value)
                        }
                        className="text-sm font-mono text-ink-bright hover:text-accent transition-colors cursor-pointer"
                        title={
                          inDistributorMode ? 'Click to edit override' : 'Click to edit base value'
                        }
                      >
                        {inDistributorMode && !override ? (
                          <span className="text-ink-muted italic">(inherit)</span>
                        ) : (
                          `$${valueDisplay}`
                        )}
                      </button>
                      {inDistributorMode && override && (
                        <button
                          onClick={() => setConfirmRemove(baseRow)}
                          disabled={busy}
                          className="arbi-btn-ghost text-2xs px-1.5 py-0.5"
                          title="Remove override (revert to base value)"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* applies_to_paths chips (click to toggle) */}
                <div className="flex flex-wrap gap-1 max-w-xs">
                  {effectiveRow.applies_to_paths.length === 0 ? (
                    <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-surface-3 text-ink-muted">
                      All paths
                    </span>
                  ) : null}
                  {activePaths.map((p) => {
                    const selected = effectiveRow.applies_to_paths.includes(p.path_key);
                    return (
                      <button
                        key={p.path_key}
                        onClick={() => void toggleAppliesToPath(baseRow, p.path_key)}
                        disabled={busy}
                        className={
                          selected
                            ? 'px-1.5 py-0.5 rounded text-2xs font-medium bg-accent/20 text-accent hover:bg-accent/30 transition-colors'
                            : 'px-1.5 py-0.5 rounded text-2xs font-medium bg-surface-3 text-ink-muted hover:bg-surface-4 transition-colors'
                        }
                        title={`${selected ? 'Remove from' : 'Add to'} applies_to_paths`}
                      >
                        {p.display_name}
                      </button>
                    );
                  })}
                </div>

                {/* is_active toggle */}
                <ArbiToggle
                  checked={effectiveRow.is_active}
                  onChange={() => void toggleActive(baseRow)}
                  disabled={busy}
                  label={`Toggle ${baseRow.display_name} active`}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* ConfirmDialog gate for Remove override (per build prompt §3) */}
      {confirmRemove && (
        <ConfirmDialog
          open={true}
          title="Remove override?"
          message={`Remove the ${confirmRemove.display_name} override for this distributor? The engine will revert to the base value ($${confirmRemove.value.toFixed(2)}).`}
          confirmLabel="Remove override"
          onConfirm={() => void removeOverride()}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </SectionCard>
  );
}

// ── Distributor Scope section RETIRED — U10 SLICE B (20260719000045) ────────
// domain_scope_config dropped (fork 3, Sol "go."): dormant machinery — 0 rows
// live since the U1 wipe, selector-visibility already fail-safe-hidden at 0 rows;
// the section + its types/toggle/provenance display retired WITH the table
// (register: the U10 SLICE B build entry; the ArbiToggle write-on-change +
// setBusy pattern lives on in the Day-93 (a)-shell helper below).

// ── INTEGRATIONS > Algopix ────────────────────────────────────────────────────

interface SectionRefsOnlyProps {
  sectionRefs: SectionRefs;
}

// ── Distributor Capabilities section (U10 A2 — BACKLOG:315 [ANNOTATED AR-0145: DEAD at HEAD; anchor by content — "U10 A2 - the per-distributor capability registry editor"]a) ─────────────────
// Per-distributor capability registry editor over the U5 S1 substrate
// (20260708000022): distributor_capability rows keyed UNIQUE(distributor_id,
// direction), direction ∈ inbound_cost_catalog | outbound_orders |
// return_to_distributor. Deny-first: an ABSENT row = unsupported (capability-
// absent → existing fallback), so the grid renders all three directions per
// distributor and creates the row on first edit (INSERT is admin-RLS'd,
// distributor_capability_insert_admin). Writes are RLS-guarded direct table
// writes (distributor_capability_update_admin, is_admin()) — non-money admin
// config, LWW-by-design (the :309 params-LWW class; the U4 CAS guard covers
// money fields only). The config JSONB editor is the :306 BLVS deferred-
// details discharge path (API-connect + RMA terms fill piecewise as BLVS
// answers arrive — previously a hand UPDATE).

const CAPABILITY_DIRECTIONS = [
  'inbound_cost_catalog',
  'outbound_orders',
  'return_to_distributor',
] as const;
type CapabilityDirection = (typeof CAPABILITY_DIRECTIONS)[number];

const CAPABILITY_DIRECTION_LABELS: Record<CapabilityDirection, string> = {
  inbound_cost_catalog: 'Inbound cost catalog',
  outbound_orders: 'Outbound orders',
  return_to_distributor: 'Return to distributor',
};

interface CapabilityRow {
  id: string;
  distributor_id: string;
  direction: CapabilityDirection;
  is_supported: boolean;
  config: Record<string, unknown> | null;
  updated_at: string;
}

interface CapabilityDistributor {
  id: string;
  name: string;
  code: string;
}

interface CapabilityConfigDraft {
  dist: CapabilityDistributor;
  direction: CapabilityDirection;
  row: CapabilityRow | null;
  text: string;
}

function DistributorCapabilitiesSection({
  sectionRefs,
  showToast,
}: SectionWithToastProps): JSX.Element {
  const [distributors, setDistributors] = useState<CapabilityDistributor[]>([]);
  const [capRows, setCapRows] = useState<CapabilityRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);
  const [confirmOff, setConfirmOff] = useState<{
    dist: CapabilityDistributor;
    row: CapabilityRow;
  } | null>(null);
  const [configDraft, setConfigDraft] = useState<CapabilityConfigDraft | null>(null);
  const [configErr, setConfigErr] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    const [dRes, cRes] = await Promise.all([
      supabase.from('distributors').select('id, name, code').order('name', { ascending: true }),
      supabase
        .from('distributor_capability')
        .select('id, distributor_id, direction, is_supported, config, updated_at'),
    ]);
    if (dRes.error) {
      showToast({ kind: 'error', message: `Distributors load failed — ${dRes.error.message}` });
      return;
    }
    if (cRes.error) {
      showToast({ kind: 'error', message: `Capabilities load failed — ${cRes.error.message}` });
      return;
    }
    setDistributors((dRes.data ?? []) as unknown as CapabilityDistributor[]);
    setCapRows((cRes.data ?? []) as unknown as CapabilityRow[]);
  }, [showToast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadAll();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAll]);

  function rowFor(distributorId: string, direction: CapabilityDirection): CapabilityRow | null {
    return (
      capRows.find((r) => r.distributor_id === distributorId && r.direction === direction) ?? null
    );
  }

  async function setSupported(
    dist: CapabilityDistributor,
    direction: CapabilityDirection,
    row: CapabilityRow | null,
    next: boolean
  ): Promise<void> {
    setBusy(true);
    const { error } = row
      ? await supabase
          .from('distributor_capability')
          .update({ is_supported: next, updated_at: new Date().toISOString() } as never)
          .eq('id', row.id)
      : await supabase
          .from('distributor_capability')
          .insert({ distributor_id: dist.id, direction, is_supported: next } as never);
    setBusy(false);
    if (error) {
      showToast({ kind: 'error', message: `Capability update failed — ${error.message}` });
      return;
    }
    showToast({
      kind: 'success',
      message: `${dist.name} · ${CAPABILITY_DIRECTION_LABELS[direction]} ${next ? 'supported' : 'unsupported'}`,
    });
    await loadAll();
  }

  function requestToggle(dist: CapabilityDistributor, direction: CapabilityDirection): void {
    const row = rowFor(dist.id, direction);
    if (row?.is_supported) setConfirmOff({ dist, row });
    else void setSupported(dist, direction, row, true);
  }

  function openConfigEditor(dist: CapabilityDistributor, direction: CapabilityDirection): void {
    const row = rowFor(dist.id, direction);
    setConfigErr(null);
    setConfigDraft({ dist, direction, row, text: JSON.stringify(row?.config ?? {}, null, 2) });
  }

  async function saveConfig(): Promise<void> {
    if (!configDraft) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(configDraft.text);
    } catch (e) {
      setConfigErr(`Invalid JSON — ${(e as Error).message}`);
      return;
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setConfigErr('Config must be a JSON object');
      return;
    }
    setBusy(true);
    const { error } = configDraft.row
      ? await supabase
          .from('distributor_capability')
          .update({ config: parsed, updated_at: new Date().toISOString() } as never)
          .eq('id', configDraft.row.id)
      : await supabase.from('distributor_capability').insert({
          distributor_id: configDraft.dist.id,
          direction: configDraft.direction,
          is_supported: false,
          config: parsed,
        } as never);
    setBusy(false);
    if (error) {
      showToast({ kind: 'error', message: `Config save failed — ${error.message}` });
      return;
    }
    showToast({
      kind: 'success',
      message: `${configDraft.dist.name} · ${CAPABILITY_DIRECTION_LABELS[configDraft.direction]} config saved`,
    });
    setConfigDraft(null);
    await loadAll();
  }

  return (
    <div
      id="section-distributor-capabilities"
      ref={(el) => {
        if (sectionRefs) sectionRefs.current['distributor-capabilities'] = el;
      }}
      className="space-y-4"
    >
      <div className="bg-surface-1 border border-border rounded-lg">
        <div className="px-4 py-3 border-b border-border-subtle">
          <h2 className="text-sm font-semibold text-ink-bright">Distributor Capabilities</h2>
          <p className="text-xs text-ink-muted mt-1">
            Per-direction API capability per distributor. Deny-first: a direction with no row (or
            toggled off) falls back to the existing manual flow. Config holds connection details as
            they arrive (e.g. the BLVS API-connect + RMA terms fill) — edits apply immediately.
          </p>
        </div>
        <div className="px-3 py-1 divide-y divide-border-subtle">
          {loading ? (
            <div className="py-6 text-center text-sm text-ink-muted">Loading…</div>
          ) : distributors.length === 0 ? (
            <div className="py-6 text-center text-sm text-ink-muted">No distributors</div>
          ) : (
            distributors.map((dist) => (
              <div key={dist.id} className="py-2.5 px-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-ink-primary">{dist.name}</span>
                  <span className="text-2xs text-ink-muted font-mono">{dist.code}</span>
                </div>
                <div className="mt-1.5 space-y-1.5">
                  {CAPABILITY_DIRECTIONS.map((direction) => {
                    const row = rowFor(dist.id, direction);
                    const isEditing =
                      configDraft !== null &&
                      configDraft.dist.id === dist.id &&
                      configDraft.direction === direction;
                    return (
                      <div key={direction}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs text-ink-secondary">
                              {CAPABILITY_DIRECTION_LABELS[direction]}
                            </span>
                            {row === null && (
                              <span className="text-2xs text-ink-muted">
                                no row — defaults to unsupported
                              </span>
                            )}
                            {row !== null && row.config !== null && (
                              <span className="text-2xs text-ink-muted font-mono">config set</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => openConfigEditor(dist, direction)}
                              disabled={busy}
                              className="arbi-btn-ghost text-2xs px-1.5 py-0.5"
                            >
                              Edit config
                            </button>
                            <ArbiToggle
                              checked={row?.is_supported ?? false}
                              onChange={() => requestToggle(dist, direction)}
                              label={`Toggle ${dist.name} ${CAPABILITY_DIRECTION_LABELS[direction]}`}
                            />
                          </div>
                        </div>
                        {isEditing && configDraft !== null && (
                          <div className="mt-1.5 space-y-1.5">
                            <textarea
                              value={configDraft.text}
                              onChange={(e) =>
                                setConfigDraft({ ...configDraft, text: e.target.value })
                              }
                              className="arbi-input w-full text-xs font-mono resize-y"
                              rows={6}
                              disabled={busy}
                              aria-label={`${dist.name} ${CAPABILITY_DIRECTION_LABELS[direction]} config JSON`}
                            />
                            {configErr !== null && (
                              <p className="text-xs text-status-error">{configErr}</p>
                            )}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => void saveConfig()}
                                disabled={busy}
                                className="arbi-btn-primary text-xs px-2 py-1"
                              >
                                Save config
                              </button>
                              <button
                                onClick={() => {
                                  setConfigDraft(null);
                                  setConfigErr(null);
                                }}
                                disabled={busy}
                                className="arbi-btn-ghost text-xs px-2 py-1"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ConfirmDialog gate on un-support only (deny-first: enabling is the safe
          direction; disabling flips the engine back to fallback — Variant A copy) */}
      <ConfirmDialog
        open={confirmOff !== null}
        title={
          confirmOff
            ? `Mark ${CAPABILITY_DIRECTION_LABELS[confirmOff.row.direction]} unsupported`
            : ''
        }
        message={
          confirmOff
            ? `${confirmOff.dist.name} falls back to the manual flow for this direction. Config is kept. Re-enable from this section anytime.`
            : ''
        }
        confirmLabel={confirmOff ? 'Mark unsupported' : 'Confirm'}
        onConfirm={() => {
          const cur = confirmOff;
          setConfirmOff(null);
          if (!cur) return;
          void setSupported(cur.dist, cur.row.direction, cur.row, false);
        }}
        onCancel={() => setConfirmOff(null)}
      />
    </div>
  );
}

function AlgopixSection({ sectionRefs }: SectionRefsOnlyProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <SectionCard id="algopix" title="Algopix" sectionRefs={sectionRefs}>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded text-2xs font-medium bg-success/15 text-success">
            Manual Upload Mode
          </span>
        </div>
        <p className="text-sm text-ink-secondary">
          Algopix data is ingested via file upload in the Files section
        </p>
        <button onClick={() => navigate('/files')} className="arbi-btn-ghost text-sm">
          Go to Files <ExternalLink size={12} />
        </button>
      </div>
    </SectionCard>
  );
}

// ── SYSTEM > API Keys (admin only) ───────────────────────────────────────────

interface ApiKeyRow {
  id: string;
  label: string;
  key_prefix: string;
  created_by: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

function generateApiKeyPlaintext(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  // base64url-encode without padding so the key is URL-safe and copyable as a single token.
  const base64 = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `arbi_${base64}`;
}

async function sha256Hex(input: string): Promise<string> {
  const encoded = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function ApiKeysSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  const { user } = useApp();
  const [rows, setRows] = useState<ApiKeyRow[]>([]);
  const [profileNames, setProfileNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);

  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [createLabel, setCreateLabel] = useState<string>('');
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null);

  const labelRef = useRef<HTMLInputElement | null>(null);

  const loadRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await supabase
      .from('api_keys')
      .select('id, label, key_prefix, created_by, created_at, last_used_at, revoked_at')
      .order('created_at', { ascending: false });
    if (error) {
      showToast({ kind: 'error', message: `Failed to load API keys — ${error.message}` });
      setLoading(false);
      return;
    }
    const keyRows = (data ?? []) as ApiKeyRow[];
    setRows(keyRows);

    // Two-step created_by → full_name resolution (created_by FKs auth.users; resolved via profiles).
    const distinctIds = Array.from(
      new Set(keyRows.map((r) => r.created_by).filter((id): id is string => id !== null))
    );
    if (distinctIds.length > 0) {
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', distinctIds);
      const nameMap: Record<string, string> = {};
      for (const p of (profilesData ?? []) as Array<{ id: string; full_name: string | null }>) {
        if (p.full_name) nameMap[p.id] = p.full_name;
      }
      setProfileNames(nameMap);
    } else {
      setProfileNames({});
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (createOpen && !revealedKey && labelRef.current) labelRef.current.focus();
  }, [createOpen, revealedKey]);

  // Escape key dismisses modal in form state only — once a key is revealed
  // the only exit is the Done button so the user can't accidentally close
  // the panel without seeing the warning.
  useEffect(() => {
    if (!createOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !revealedKey) closeCreate();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen, revealedKey]);

  function openCreate(): void {
    setCreateLabel('');
    setCreateErr(null);
    setRevealedKey(null);
    setCopied(false);
    setCreateOpen(true);
  }

  function closeCreate(): void {
    setCreateOpen(false);
    setCreateLabel('');
    setCreateErr(null);
    setRevealedKey(null);
    setCopied(false);
  }

  async function submitCreate(): Promise<void> {
    const trimmed = createLabel.trim();
    if (!trimmed) {
      setCreateErr('Enter a label for this key');
      return;
    }
    setBusy(true);
    setCreateErr(null);
    try {
      const plaintext = generateApiKeyPlaintext();
      const keyPrefix = plaintext.slice(0, 8);
      const keyHash = await sha256Hex(plaintext);

      const { error } = await supabase.from('api_keys').insert({
        label: trimmed,
        key_prefix: keyPrefix,
        key_hash: keyHash,
        created_by: user?.id ?? null,
      } as never);
      if (error) {
        setCreateErr(`Failed to create key — ${error.message}`);
        setBusy(false);
        return;
      }
      setRevealedKey(plaintext);
      setBusy(false);
      await loadRows();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown error';
      setCreateErr(`Failed to create key — ${msg}`);
      setBusy(false);
    }
  }

  async function copyRevealedKey(): Promise<void> {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast({ kind: 'error', message: 'Copy to clipboard failed' });
    }
  }

  async function confirmRevoke(): Promise<void> {
    const target = revokeTarget;
    if (!target) return;
    setRevokeTarget(null);
    setBusy(true);
    const { error } = await supabase
      .from('api_keys')
      .update({ revoked_at: new Date().toISOString() } as never)
      .eq('id', target.id);
    setBusy(false);
    if (error) {
      showToast({ kind: 'error', message: `Failed to revoke key — ${error.message}` });
      return;
    }
    showToast({ kind: 'success', message: 'API key revoked' });
    await loadRows();
  }

  function handleLabelKeyDown(e: ReactKeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' && !busy) {
      e.preventDefault();
      void submitCreate();
    }
  }

  return (
    <SectionCard id="api-keys" title="API Keys" sectionRefs={sectionRefs}>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-ink-secondary">
            Generate keys to authenticate external integrations with Arbi.
          </p>
          <button
            onClick={openCreate}
            className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5"
          >
            New API key
          </button>
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-ink-muted">Loading API keys…</div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Key size={48} className="text-ink-muted mb-3" />
            <p className="text-sm text-ink-bright">No API keys created yet</p>
            <p className="text-xs text-ink-muted mt-1">
              Generate a key to authenticate external integrations.
            </p>
          </div>
        ) : (
          <div className="space-y-0 divide-y divide-border-subtle">
            <div className="flex items-center gap-3 pb-2 text-2xs uppercase tracking-wider text-ink-muted">
              <div className="flex-1">Label</div>
              <div className="w-32">Prefix</div>
              <div className="w-32">Created by</div>
              <div className="w-24">Created</div>
              <div className="w-24">Last used</div>
              <div className="w-20">Status</div>
              <div className="w-20" />
            </div>
            {rows.map((row) => {
              const active = row.revoked_at === null;
              const createdByName = row.created_by ? profileNames[row.created_by] : null;
              return (
                <div key={row.id} className="flex items-center gap-3 py-2.5">
                  <div className="flex-1 text-sm text-ink-bright">{row.label}</div>
                  <div className="w-32 font-mono text-xs text-ink-secondary">{row.key_prefix}…</div>
                  <div className="w-32 text-xs text-ink-secondary">
                    {createdByName ?? (row.created_by ? '(unknown)' : '—')}
                  </div>
                  <div className="w-24 text-xs text-ink-secondary">
                    {formatLastSignIn(row.created_at)}
                  </div>
                  <div className="w-24 text-xs text-ink-secondary">
                    {formatLastSignIn(row.last_used_at)}
                  </div>
                  <div className="w-20">
                    {active ? (
                      <span className="px-2 py-0.5 rounded text-2xs font-medium bg-success/15 text-success">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-2xs font-medium bg-surface-4 text-ink-muted">
                        Revoked
                      </span>
                    )}
                  </div>
                  <div className="w-20 text-right">
                    {active && (
                      <button
                        onClick={() => setRevokeTarget(row)}
                        disabled={busy}
                        className="arbi-btn-ghost text-xs text-danger hover:bg-danger/10 px-2 py-1 disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / one-time-reveal modal (mirrors ConfirmDialog shell shape) */}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={revealedKey ? undefined : closeCreate}
            aria-hidden="true"
          />
          <div className="relative bg-surface-2 border border-border rounded-lg shadow-xl w-96 p-5 space-y-4">
            {!revealedKey ? (
              <>
                <h3 className="text-sm font-semibold text-ink-bright">Generate API key</h3>
                <div className="space-y-1.5">
                  <label htmlFor="api-key-label" className="text-xs text-ink-secondary block">
                    Label
                  </label>
                  <input
                    ref={labelRef}
                    id="api-key-label"
                    type="text"
                    value={createLabel}
                    onChange={(e) => setCreateLabel(e.target.value)}
                    onKeyDown={handleLabelKeyDown}
                    placeholder="e.g. Repricer integration"
                    className="arbi-input w-full text-sm"
                    disabled={busy}
                  />
                  <p className="text-2xs text-ink-muted">
                    A short name to identify this key in the list.
                  </p>
                  {createErr && (
                    <p className="text-xs text-danger" role="alert">
                      {createErr}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    onClick={closeCreate}
                    disabled={busy}
                    className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1.5 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => void submitCreate()}
                    disabled={busy || !createLabel.trim()}
                    className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5 disabled:opacity-50"
                  >
                    {busy ? 'Generating…' : 'Generate key'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-sm font-semibold text-ink-bright">API key generated</h3>
                <div className="bg-warning/10 border border-warning/30 rounded p-3 text-xs text-ink-bright">
                  <strong className="font-semibold">Save this key now.</strong> It won&apos;t be
                  shown again — Arbi only stores its hash.
                </div>
                <div className="space-y-1.5">
                  <div className="text-xs text-ink-secondary">Your new key</div>
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1 bg-surface-1 border border-border rounded px-3 py-2 font-mono text-xs text-ink-bright break-all">
                      {revealedKey}
                    </div>
                    <button
                      onClick={() => void copyRevealedKey()}
                      title="Copy to clipboard"
                      className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-2"
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-end pt-1">
                  <button
                    onClick={closeCreate}
                    className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Revoke confirmation — ConfirmDialog Variant A shape (Day 86 R5 /
          Day 89 lock): entity name in title (no "?" terminator), entity
          name echoed in confirmLabel. Reversibility note slot replaced by
          explicit irreversibility per TONE_STANDARDS L203 canonical
          phrasing — revoke cannot be reversed (a new key can be generated
          but the original secret can't be recovered). */}
      <ConfirmDialog
        open={revokeTarget !== null}
        title={revokeTarget ? `Revoke ${revokeTarget.label}` : ''}
        message="Revoking immediately invalidates this key. Integrations using it will stop authenticating. Generate a new key to replace it. This cannot be undone."
        confirmLabel={revokeTarget ? `Revoke ${revokeTarget.label}` : 'Revoke'}
        onConfirm={() => void confirmRevoke()}
        onCancel={() => setRevokeTarget(null)}
      />
    </SectionCard>
  );
}

// ── SYSTEM > Audit Trail ──────────────────────────────────────────────────────

type AuditEditType = 'config' | 'status_transition' | 'financial' | 'security';

interface AuditLogRow {
  id: string;
  edit_type: AuditEditType;
  entity_type: string;
  entity_id: string | null;
  action: string;
  actor_id: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

const AUDIT_PAGE_SIZE = 50;

const EDIT_TYPE_FILTER_OPTIONS: ArbiSelectOption<string>[] = [
  { value: 'all', label: 'All event types' },
  { value: 'config', label: 'Config' },
  { value: 'status_transition', label: 'Status transition' },
  { value: 'financial', label: 'Financial' },
  { value: 'security', label: 'Security' },
];

const EDIT_TYPE_BADGE: Record<AuditEditType, string> = {
  config: 'bg-info/15 text-info',
  status_transition: 'bg-accent/15 text-accent',
  financial: 'bg-warning/15 text-warning',
  security: 'bg-danger/15 text-danger',
};

const EDIT_TYPE_LABEL: Record<AuditEditType, string> = {
  config: 'Config',
  status_transition: 'Status',
  financial: 'Financial',
  security: 'Security',
};

function formatAuditDetail(detail: AuditLogRow['detail']): string {
  if (!detail || typeof detail !== 'object') return '—';
  const entries = Object.entries(detail);
  if (entries.length === 0) return '—';
  return entries
    .map(([k, v]) => {
      const val = v === null ? 'null' : typeof v === 'object' ? JSON.stringify(v) : String(v);
      return `${k}: ${val}`;
    })
    .join('  ·  ');
}

function AuditTrailSection({ sectionRefs }: SectionRefsOnlyProps): JSX.Element {
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [editTypeFilter, setEditTypeFilter] = useState<AuditEditType | null>(null);
  const [actorFilter, setActorFilter] = useState<string | null>(null);

  const [actorNames, setActorNames] = useState<Record<string, string>>({});
  const [actorOptions, setActorOptions] = useState<ArbiSelectOption<string>[]>([
    { value: 'all', label: 'All actors' },
  ]);

  const hasActiveFilters = editTypeFilter !== null || actorFilter !== null;

  // Load actor dropdown options once on mount. Sourced from profiles so the
  // dropdown reflects real users; per Sol amendment 2, no speculative
  // vocabulary. entity_type filter intentionally omitted at V1 — when the
  // audit_log accumulates downstream a future build day can derive its
  // options from loaded data.
  const loadActors = useCallback(async (): Promise<void> => {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name')
      .order('full_name', { ascending: true });
    const profiles = (data ?? []) as Array<{ id: string; full_name: string | null }>;
    const nameMap: Record<string, string> = {};
    const options: ArbiSelectOption<string>[] = [{ value: 'all', label: 'All actors' }];
    for (const p of profiles) {
      const label = p.full_name ?? p.id.slice(0, 8);
      nameMap[p.id] = label;
      options.push({ value: p.id, label });
    }
    setActorNames(nameMap);
    setActorOptions(options);
  }, []);

  const loadPage = useCallback(
    async (
      offset: number,
      currentEditType: AuditEditType | null,
      currentActor: string | null
    ): Promise<AuditLogRow[] | null> => {
      let query = supabase
        .from('audit_log')
        .select('id, edit_type, entity_type, entity_id, action, actor_id, detail, created_at')
        .order('created_at', { ascending: false })
        .range(offset, offset + AUDIT_PAGE_SIZE - 1);
      if (currentEditType !== null) query = query.eq('edit_type', currentEditType);
      if (currentActor !== null) query = query.eq('actor_id', currentActor);

      const { data, error: err } = await query;
      if (err) {
        setError(err.message);
        return null;
      }
      return (data ?? []) as AuditLogRow[];
    },
    []
  );

  const loadInitial = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    const data = await loadPage(0, editTypeFilter, actorFilter);
    if (data === null) {
      setRows([]);
      setHasMore(false);
      setLoading(false);
      return;
    }
    setRows(data);
    setHasMore(data.length === AUDIT_PAGE_SIZE);
    setLoading(false);
  }, [loadPage, editTypeFilter, actorFilter]);

  const loadMore = useCallback(async (): Promise<void> => {
    setLoadingMore(true);
    const data = await loadPage(rows.length, editTypeFilter, actorFilter);
    if (data === null) {
      setLoadingMore(false);
      return;
    }
    setRows((prev) => [...prev, ...data]);
    setHasMore(data.length === AUDIT_PAGE_SIZE);
    setLoadingMore(false);
  }, [loadPage, rows.length, editTypeFilter, actorFilter]);

  useEffect(() => {
    void loadActors();
  }, [loadActors]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  function clearFilters(): void {
    setEditTypeFilter(null);
    setActorFilter(null);
  }

  function handleEditTypeChange(value: string): void {
    setEditTypeFilter(value === 'all' ? null : (value as AuditEditType));
  }

  function handleActorChange(value: string): void {
    setActorFilter(value === 'all' ? null : value);
  }

  return (
    <SectionCard id="audit-trail" title="Audit Trail" sectionRefs={sectionRefs}>
      <div className="space-y-3">
        {/* Filter toolbar */}
        <div className="flex items-center gap-3">
          <ArbiSelect
            value={editTypeFilter ?? 'all'}
            options={EDIT_TYPE_FILTER_OPTIONS}
            onChange={handleEditTypeChange}
            size="xs"
            className="w-44"
          />
          <ArbiSelect
            value={actorFilter ?? 'all'}
            options={actorOptions}
            onChange={handleActorChange}
            size="xs"
            className="w-44"
          />
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="arbi-btn-ghost text-xs px-2 py-1"
              type="button"
            >
              Clear filters
            </button>
          )}
        </div>

        {error ? (
          <div className="py-8 text-center">
            <p className="text-sm text-danger">Failed to load audit log — {error}</p>
          </div>
        ) : loading ? (
          <div className="py-8 text-center text-sm text-ink-muted">Loading audit events…</div>
        ) : rows.length === 0 ? (
          hasActiveFilters ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <ClipboardList size={48} className="text-ink-muted mb-3" />
              <p className="text-sm text-ink-bright">No events match your current filters</p>
              <button
                onClick={clearFilters}
                className="arbi-btn-ghost text-xs px-2 py-1 mt-2"
                type="button"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <ClipboardList size={48} className="text-ink-muted mb-3" />
              <p className="text-sm text-ink-bright">No audit events recorded yet</p>
              <p className="text-xs text-ink-muted mt-1">
                Events will appear here as tracked edits occur.
              </p>
            </div>
          )
        ) : (
          <>
            <div className="space-y-0 divide-y divide-border-subtle">
              <div className="flex items-center gap-3 pb-2 text-2xs uppercase tracking-wider text-ink-muted">
                <div className="w-28">When</div>
                <div className="w-28">Type</div>
                <div className="w-28">Entity</div>
                <div className="w-28">Action</div>
                <div className="w-28">Actor</div>
                <div className="flex-1">Detail</div>
              </div>
              {rows.map((row) => {
                const actorLabel = row.actor_id
                  ? (actorNames[row.actor_id] ?? row.actor_id.slice(0, 8))
                  : 'system';
                return (
                  <div key={row.id} className="flex items-start gap-3 py-2.5">
                    <div
                      className="w-28 text-xs text-ink-secondary"
                      title={new Date(row.created_at).toLocaleString()}
                    >
                      {formatLastSignIn(row.created_at)}
                    </div>
                    <div className="w-28">
                      <span
                        className={clsx(
                          'px-2 py-0.5 rounded text-2xs font-medium',
                          EDIT_TYPE_BADGE[row.edit_type]
                        )}
                      >
                        {EDIT_TYPE_LABEL[row.edit_type]}
                      </span>
                    </div>
                    <div
                      className="w-28 text-xs text-ink-secondary truncate"
                      title={row.entity_id ?? ''}
                    >
                      {row.entity_type}
                    </div>
                    <div className="w-28 text-xs text-ink-bright truncate" title={row.action}>
                      {row.action}
                    </div>
                    <div className="w-28 text-xs text-ink-secondary truncate" title={actorLabel}>
                      {actorLabel}
                    </div>
                    <div className="flex-1 text-xs text-ink-secondary font-mono break-all">
                      {formatAuditDetail(row.detail)}
                    </div>
                  </div>
                );
              })}
            </div>
            {hasMore && (
              <div className="pt-2 text-center">
                <button
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                  className="arbi-btn-ghost text-xs px-3 py-1.5 disabled:opacity-50"
                  type="button"
                >
                  {loadingMore ? 'Loading…' : 'Show more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
}

function formatLastSignIn(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '—';
  const ageDays = (Date.now() - t) / 86400000;
  if (ageDays < 7) {
    if (ageDays < 1 / 24) return 'just now';
    if (ageDays < 1) return `${Math.floor(ageDays * 24)}h ago`;
    return `${Math.floor(ageDays)}d ago`;
  }
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── ORGANIZATION > Security (admin only) ─────────────────────────────────────

interface PendingMfaFactor {
  id: string;
  secret: string;
  uri: string;
  qrCode: string;
}

const SESSION_TIMEOUT_OPTIONS: ArbiSelectOption<number>[] = [
  { value: 5, label: '5 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
  { value: 1440, label: '24 hours' },
];

function SecuritySection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  const { organization, setOrganization } = useApp();

  const [factors, setFactors] = useState<Factor[]>([]);
  const [loadingFactors, setLoadingFactors] = useState<boolean>(true);
  const [enrollOpen, setEnrollOpen] = useState<boolean>(false);
  const [enrolling, setEnrolling] = useState<boolean>(false);
  const [pendingFactor, setPendingFactor] = useState<PendingMfaFactor | null>(null);
  const [verifyCode, setVerifyCode] = useState<string>('');
  const [verifying, setVerifying] = useState<boolean>(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Factor | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  const [timeoutValue, setTimeoutValue] = useState<number>(
    organization?.session_timeout_minutes ?? 240
  );
  const [savingTimeout, setSavingTimeout] = useState<boolean>(false);

  const verifyInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setTimeoutValue(organization?.session_timeout_minutes ?? 240);
  }, [organization?.session_timeout_minutes]);

  useEffect(() => {
    if (enrollOpen && pendingFactor && verifyInputRef.current) {
      verifyInputRef.current.focus();
    }
  }, [enrollOpen, pendingFactor]);

  // Sol Checkpoint #1 A2 — auto-unenroll stale unverified factors on load
  // rather than offering resume-verify. Mid-enroll abandonment leaves an
  // unverified factor lingering; clearing on next mount keeps the section's
  // state machine in {none-enrolled, verified-enrolled} without an ambiguous
  // third state.
  const loadFactors = useCallback(async (): Promise<void> => {
    setLoadingFactors(true);
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error || !data) {
      setLoadingFactors(false);
      return;
    }
    const totp = (data.totp ?? []) as Factor[];
    const unverified = totp.filter((f) => f.status === 'unverified');
    if (unverified.length > 0) {
      for (const f of unverified) {
        await supabase.auth.mfa.unenroll({ factorId: f.id });
      }
      const { data: refreshed } = await supabase.auth.mfa.listFactors();
      setFactors((refreshed?.totp ?? []) as Factor[]);
    } else {
      setFactors(totp);
    }
    setLoadingFactors(false);
  }, []);

  useEffect(() => {
    void loadFactors();
  }, [loadFactors]);

  async function startEnroll(): Promise<void> {
    setEnrolling(true);
    setVerifyError(null);
    setVerifyCode('');
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: `Arbi (${new Date().toISOString().slice(0, 10)})`,
    });
    setEnrolling(false);
    if (error || !data) {
      showToast({
        kind: 'error',
        message: `Failed to start enrollment — ${error?.message ?? 'unknown error'}`,
      });
      return;
    }
    setPendingFactor({
      id: data.id,
      secret: data.totp.secret,
      uri: data.totp.uri,
      qrCode: data.totp.qr_code,
    });
    setEnrollOpen(true);
  }

  async function cancelEnroll(): Promise<void> {
    if (pendingFactor) {
      // Unenroll the unverified factor we just minted. A2 auto-cleanup would
      // catch this on next mount anyway, but cleaning eagerly keeps the
      // listFactors surface accurate for the rest of this session.
      await supabase.auth.mfa.unenroll({ factorId: pendingFactor.id });
    }
    setEnrollOpen(false);
    setPendingFactor(null);
    setVerifyCode('');
    setVerifyError(null);
  }

  async function submitVerify(): Promise<void> {
    if (!pendingFactor) return;
    const code = verifyCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setVerifyError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setVerifying(true);
    setVerifyError(null);
    const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({
      factorId: pendingFactor.id,
    });
    if (chErr || !challenge) {
      setVerifying(false);
      setVerifyError(chErr?.message ?? 'Failed to start challenge');
      return;
    }
    const { error: verErr } = await supabase.auth.mfa.verify({
      factorId: pendingFactor.id,
      challengeId: challenge.id,
      code,
    });
    setVerifying(false);
    if (verErr) {
      setVerifyError(verErr.message ?? 'Verification failed');
      return;
    }
    setEnrollOpen(false);
    setPendingFactor(null);
    setVerifyCode('');
    showToast({ kind: 'success', message: 'Two-factor authentication enabled' });
    await loadFactors();
  }

  async function confirmRemove(): Promise<void> {
    if (!removeTarget) return;
    const target = removeTarget;
    setRemoveTarget(null);
    setRemoving(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId: target.id });
    setRemoving(false);
    if (error) {
      showToast({
        kind: 'error',
        message: `Failed to remove two-factor — ${error.message}`,
      });
      return;
    }
    showToast({ kind: 'success', message: 'Two-factor authentication removed' });
    await loadFactors();
  }

  async function saveSessionTimeout(value: number): Promise<void> {
    setTimeoutValue(value);
    setSavingTimeout(true);
    const { error } = await supabase
      .from('organization')
      .update({ session_timeout_minutes: value } as never)
      .eq('id', 1);
    setSavingTimeout(false);
    if (error) {
      showToast({ kind: 'error', message: `Failed to save — ${error.message}` });
      return;
    }
    if (organization) {
      setOrganization({ ...organization, session_timeout_minutes: value });
    }
    showToast({ kind: 'success', message: 'Session timeout updated' });
  }

  const enrolled = factors.find((f) => f.status === 'verified');

  return (
    <SectionCard id="security" title="Security" sectionRefs={sectionRefs}>
      <div className="space-y-5">
        {/* Two-Factor Authentication block */}
        <div>
          <h3 className="text-sm font-semibold text-ink-bright mb-2">Two-Factor Authentication</h3>
          {loadingFactors ? (
            <p className="text-xs text-ink-muted">Loading…</p>
          ) : enrolled ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="px-2 py-0.5 rounded text-2xs font-medium bg-success/15 text-success shrink-0">
                  Enabled
                </span>
                <span className="text-xs text-ink-secondary truncate">
                  {enrolled.friendly_name || 'Authenticator app'} · added{' '}
                  {formatLastSignIn(enrolled.created_at)}
                </span>
              </div>
              <button
                onClick={() => setRemoveTarget(enrolled)}
                disabled={removing}
                className="arbi-btn-ghost text-xs text-danger hover:bg-danger/10 px-2 py-1 disabled:opacity-50"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-ink-secondary flex-1">
                Add an authenticator app (Google Authenticator, 1Password, Authy) for an extra
                sign-in step.
              </p>
              <button
                onClick={() => void startEnroll()}
                disabled={enrolling}
                className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5 disabled:opacity-50"
              >
                {enrolling ? 'Starting…' : 'Enroll'}
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-border-subtle" />

        {/* Session timeout block */}
        <div>
          <h3 className="text-sm font-semibold text-ink-bright mb-2">Session timeout</h3>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-ink-secondary flex-1">
              Sign out users automatically after a period of inactivity. Applies to everyone in the
              organization.
            </p>
            <ArbiSelect
              value={timeoutValue}
              options={SESSION_TIMEOUT_OPTIONS}
              onChange={(v) => void saveSessionTimeout(v)}
              size="sm"
              className="w-36"
              disabled={savingTimeout}
            />
          </div>
        </div>
      </div>

      {/* MFA enrollment modal */}
      {enrollOpen && pendingFactor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => void cancelEnroll()}
            aria-hidden="true"
          />
          <div className="relative bg-surface-2 border border-border rounded-lg shadow-xl w-96 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-ink-bright">Set up two-factor</h3>
            <p className="text-xs text-ink-secondary">
              Scan this QR code with your authenticator app, then enter the 6-digit code below.
            </p>
            <div className="flex items-center justify-center bg-surface-1 border border-border rounded p-3">
              <img
                src={`data:image/svg+xml;utf-8,${encodeURIComponent(pendingFactor.qrCode)}`}
                alt="MFA QR code"
                width={192}
                height={192}
              />
            </div>
            <details className="text-xs text-ink-muted">
              <summary className="cursor-pointer hover:text-ink-secondary">
                Can&apos;t scan? Enter the code manually
              </summary>
              <div className="mt-1.5 font-mono text-xs break-all bg-surface-1 border border-border rounded px-2 py-1.5 text-ink-bright">
                {pendingFactor.secret}
              </div>
            </details>
            <div className="space-y-1.5">
              <label htmlFor="mfa-verify-code" className="text-xs text-ink-secondary block">
                6-digit code
              </label>
              <input
                ref={verifyInputRef}
                id="mfa-verify-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="arbi-input w-full text-sm font-mono"
                disabled={verifying}
              />
              {verifyError && (
                <p className="text-xs text-danger" role="alert">
                  {verifyError}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => void cancelEnroll()}
                disabled={verifying}
                className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1.5 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => void submitVerify()}
                disabled={verifying || verifyCode.length !== 6}
                className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5 disabled:opacity-50"
              >
                {verifying ? 'Verifying…' : 'Verify and enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove confirmation — ConfirmDialog Variant A shape */}
      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove two-factor authentication"
        message="Your account will sign in with just a password after this. You can re-enable two-factor at any time."
        confirmLabel="Remove two-factor"
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoveTarget(null)}
      />
    </SectionCard>
  );
}

// ── ORGANIZATION > Notifications & Alerts ─────────────────────────────────

const CHANNELS = [
  { key: 'in_app', label: 'In-App', enabled: true },
  { key: 'email', label: 'Email', enabled: true },
  { key: 'whatsapp', label: 'WhatsApp', enabled: false, tooltip: 'Coming soon' },
  { key: 'telegram', label: 'Telegram', enabled: false, tooltip: 'Coming soon' },
];

function NotificationsSection({ sectionRefs }: SectionRefsOnlyProps): JSX.Element {
  const { user } = useApp();
  const { isEnabled, updatePreference, loading } = useNotificationPreferences(user?.id);

  return (
    <div
      id="section-notifications"
      ref={(el) => {
        if (sectionRefs) sectionRefs.current['notifications'] = el;
      }}
      className="space-y-4"
    >
      {EVENT_GROUPS.map((group: NotificationEventGroup) => (
        <div key={group.label} className="bg-surface-1 border border-border rounded-lg">
          <div className="px-4 py-3 border-b border-border-subtle">
            <h2 className="text-sm font-semibold text-ink-bright">{group.label}</h2>
          </div>
          <div className="px-4 py-2">
            {/* Column headers */}
            <div className="flex items-center py-2 mb-1">
              <div className="flex-1 text-2xs uppercase tracking-wider text-ink-muted">Event</div>
              {CHANNELS.map((ch) => (
                <div
                  key={ch.key}
                  className="w-16 text-center text-2xs uppercase tracking-wider text-ink-muted"
                >
                  {ch.label}
                </div>
              ))}
            </div>

            {/* Event rows */}
            <div className="divide-y divide-border-subtle">
              {group.events.map((event) => (
                <div key={event} className="flex items-center py-2.5">
                  <div className="flex-1 text-xs text-ink-primary">
                    {EVENT_LABELS[event] || event}
                  </div>
                  {CHANNELS.map((ch) => (
                    <div key={ch.key} className="w-16 flex justify-center">
                      <ArbiToggle
                        checked={ch.enabled ? isEnabled(event, ch.key) : false}
                        onChange={(val) => updatePreference(event, ch.key, val)}
                        disabled={!ch.enabled || loading}
                        label={`${EVENT_LABELS[event] || event} ${ch.label}`}
                        tooltip={ch.tooltip}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <p className="text-2xs text-ink-muted mt-3 mb-1">Changes save automatically</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── BUSINESS RULES > PO Types ─────────────────────────────────────────────

function slugifyPoTypeName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type PoTypeModalMode = 'add' | 'edit';

interface PoTypeDraft {
  name: string;
  description: string;
  show_discount_pct: boolean;
  show_volume_tier: boolean;
  sort_order: string;
}

const EMPTY_PO_TYPE_DRAFT: PoTypeDraft = {
  name: '',
  description: '',
  show_discount_pct: false,
  show_volume_tier: false,
  sort_order: '0',
};

function draftFromRow(row: PoTypeRow): PoTypeDraft {
  return {
    name: row.name,
    description: row.description ?? '',
    show_discount_pct: row.show_discount_pct ?? false,
    show_volume_tier: row.show_volume_tier ?? false,
    sort_order: String(row.sort_order ?? 0),
  };
}

function POTypesSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  const { allPoTypes, reload } = useSettingsConfig();
  const [busy, setBusy] = useState<boolean>(false);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<PoTypeModalMode>('add');
  const [modalRow, setModalRow] = useState<PoTypeRow | null>(null);
  const [draft, setDraft] = useState<PoTypeDraft>(EMPTY_PO_TYPE_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (modalOpen && nameInputRef.current) nameInputRef.current.focus();
  }, [modalOpen]);

  function openAdd(): void {
    setModalMode('add');
    setModalRow(null);
    setDraft(EMPTY_PO_TYPE_DRAFT);
    setDraftError(null);
    setModalOpen(true);
  }

  function openEdit(row: PoTypeRow): void {
    setModalMode('edit');
    setModalRow(row);
    setDraft(draftFromRow(row));
    setDraftError(null);
    setModalOpen(true);
  }

  function closeModal(): void {
    if (busy) return;
    setModalOpen(false);
    setDraft(EMPTY_PO_TYPE_DRAFT);
    setDraftError(null);
  }

  async function submitDraft(): Promise<void> {
    const name = draft.name.trim();
    if (!name) {
      setDraftError('Enter a name for this PO type');
      return;
    }

    const sortOrder = draft.sort_order.trim();
    const sortOrderValue = sortOrder === '' ? 0 : parseInt(sortOrder, 10);
    if (!Number.isFinite(sortOrderValue)) {
      setDraftError('Sort order must be a whole number');
      return;
    }

    setBusy(true);
    setDraftError(null);

    if (modalMode === 'add') {
      const slug = slugifyPoTypeName(name);
      if (!slug) {
        setDraftError('Name must contain at least one letter or number');
        setBusy(false);
        return;
      }
      const { data: existing } = await supabase
        .from('po_types')
        .select('id')
        .eq('slug', slug)
        .maybeSingle();
      if (existing) {
        setDraftError(`A PO type with the handle "${slug}" already exists. Try a different name.`);
        setBusy(false);
        return;
      }
      const { error } = await supabase.from('po_types').insert({
        name,
        slug,
        description: draft.description.trim() || null,
        show_discount_pct: draft.show_discount_pct,
        show_volume_tier: draft.show_volume_tier,
        sort_order: sortOrderValue,
      } as never);
      setBusy(false);
      if (error) {
        setDraftError(`Failed to create \u2014 ${error.message}`);
        return;
      }
      showToast({ kind: 'success', message: `PO type "${name}" created` });
    } else {
      if (!modalRow) {
        setBusy(false);
        return;
      }
      // Slug deliberately omitted from UPDATE \u2014 immutable handle preserves
      // POBuilder.tsx:317 lookup semantics + stable-handle backlog intent.
      const { error } = await supabase
        .from('po_types')
        .update({
          name,
          description: draft.description.trim() || null,
          show_discount_pct: draft.show_discount_pct,
          show_volume_tier: draft.show_volume_tier,
          sort_order: sortOrderValue,
        } as never)
        .eq('id', modalRow.id);
      setBusy(false);
      if (error) {
        setDraftError(`Failed to save \u2014 ${error.message}`);
        return;
      }
      showToast({ kind: 'success', message: 'PO type updated' });
    }

    setModalOpen(false);
    setDraft(EMPTY_PO_TYPE_DRAFT);
    setDraftError(null);
    await reload();
  }

  async function toggleActive(id: string, currentActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('po_types')
      .update({ is_active: !currentActive } as never)
      .eq('id', id);
    if (error) {
      showToast({ kind: 'error', message: error.message });
      return;
    }
    await reload();
    showToast({ kind: 'success', message: 'PO type updated' });
  }

  function update<K extends keyof PoTypeDraft>(key: K, value: PoTypeDraft[K]): void {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  const previewSlug = modalMode === 'add' && draft.name.trim() ? slugifyPoTypeName(draft.name) : '';

  return (
    <SectionCard id="po-types" title="PO Types" sectionRefs={sectionRefs}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs text-ink-muted">
          PO Builder form fields adapt dynamically based on the selected type.
        </p>
        <button
          onClick={openAdd}
          className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5"
          type="button"
        >
          Add PO type
        </button>
      </div>
      <div className="space-y-2">
        {(allPoTypes as unknown as PoTypeRow[]).map((t) => (
          <div
            key={t.id}
            className="flex items-center justify-between py-2.5 px-2 bg-surface-2 rounded-lg"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink-primary">{t.name}</span>
                <span className="font-mono text-2xs text-ink-muted">{t.slug}</span>
              </div>
              <p className="text-xs text-ink-muted mt-0.5">{t.description || '\u2014'}</p>
              <div className="flex items-center gap-1.5 mt-1">
                {t.show_discount_pct && (
                  <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-info/15 text-info">
                    Discount %
                  </span>
                )}
                {t.show_volume_tier && (
                  <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-accent/15 text-accent">
                    Volume Tier
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => openEdit(t)}
                className="arbi-btn-ghost text-xs px-2 py-1"
                type="button"
              >
                Edit
              </button>
              <ArbiToggle
                checked={t.is_active}
                onChange={() => toggleActive(t.id, t.is_active)}
                label={`Toggle ${t.name}`}
              />
            </div>
          </div>
        ))}
        {allPoTypes.length === 0 && (
          <p className="text-sm text-ink-muted text-center py-4">No PO types configured</p>
        )}
      </div>

      {/* Add / Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} aria-hidden="true" />
          <div className="relative bg-surface-2 border border-border rounded-lg shadow-xl w-[28rem] p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-sm font-semibold text-ink-bright">
              {modalMode === 'add' ? 'Add PO type' : `Edit ${modalRow?.name ?? 'PO type'}`}
            </h3>

            <div className="space-y-1.5">
              <label htmlFor="po-type-name" className="text-xs text-ink-secondary block">
                Name
              </label>
              <input
                ref={nameInputRef}
                id="po-type-name"
                type="text"
                value={draft.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="e.g. Regular, Closeout, Sample"
                className="arbi-input w-full text-sm"
                disabled={busy}
              />
              {modalMode === 'add' && previewSlug && (
                <p className="text-2xs text-ink-muted">
                  Handle: <span className="font-mono text-ink-secondary">{previewSlug}</span>{' '}
                  (auto-generated; cannot be changed later)
                </p>
              )}
              {modalMode === 'edit' && modalRow && (
                <p className="text-2xs text-ink-muted">
                  Handle: <span className="font-mono text-ink-secondary">{modalRow.slug}</span>{' '}
                  (immutable)
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="po-type-description" className="text-xs text-ink-secondary block">
                Description
              </label>
              <textarea
                id="po-type-description"
                value={draft.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="Short description shown in the PO Builder type picker"
                className="arbi-input w-full text-sm resize-none"
                rows={2}
                disabled={busy}
              />
            </div>

            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-ink-secondary">Show Discount %</span>
                <ArbiToggle
                  checked={draft.show_discount_pct}
                  onChange={() => update('show_discount_pct', !draft.show_discount_pct)}
                  label="Show Discount %"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-ink-secondary">Show Volume Tier</span>
                <ArbiToggle
                  checked={draft.show_volume_tier}
                  onChange={() => update('show_volume_tier', !draft.show_volume_tier)}
                  label="Show Volume Tier"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="po-type-sort" className="text-xs text-ink-secondary block">
                  Sort order
                </label>
                <input
                  id="po-type-sort"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={draft.sort_order}
                  onChange={(e) => update('sort_order', e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="0"
                  className="arbi-input w-full text-sm font-mono"
                  disabled={busy}
                />
              </div>
            </div>

            {draftError && (
              <p className="text-xs text-danger" role="alert">
                {draftError}
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={closeModal}
                disabled={busy}
                className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1.5 disabled:opacity-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={() => void submitDraft()}
                disabled={busy || !draft.name.trim()}
                className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5 disabled:opacity-50"
                type="button"
              >
                {busy ? 'Saving\u2026' : modalMode === 'add' ? 'Create PO type' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ── SYSTEM > Data (danger zone) ───────────────────────────────────────────────

type ExportTableName = keyof ArbiDatabase['public']['Tables'] & string;

// Sol Checkpoint #1 Q2 GREEN — operational + entity + operator-managed config
// (50/56). Excluded: api_keys (key_hash sensitive) + audit_log + credit_audit_log
// (admin-only sensitive event logs) + profiles (PII) + sentry_events +
// sentry_fingerprints (admin-only error logs that may leak system internals).
// auth.* not addressable via PostgREST.
const EXPORT_ALLOWLIST: ExportTableName[] = [
  // Products domain
  'products',
  'product_flags',
  'product_distributors',
  'product_profitability',
  'price_overrides',
  'velocity_overrides',
  // Purchase orders
  'purchase_orders',
  'po_line_items',
  'po_actual_costs',
  'po_line_item_allocations',
  'po_staging',
  // Receiving + returns + transfers
  'receiving_records',
  'receiving_line_items',
  'returns',
  'transfers',
  'discrepancy_resolutions',
  // Inventory + listings
  'inventory_items',
  'inventory_reservations',
  'listing_links',
  // Fulfillment
  'fulfillment_orders',
  // Finance
  'credit_ledger',
  'invoices',
  'payouts',
  'finance_writeoffs',
  // Files + media
  'files',
  'photos',
  'ingestion_batches',
  // Notes + tasks + notifications
  'entity_notes',
  'tasks',
  'notifications',
  // Entities / directory
  'distributors',
  'distributor_contacts',
  'brands',
  'categories',
  'manufacturers',
  'manufacturer_distributors',
  'warehouses',
  'marketplaces',
  // Sessions
  'buying_sessions',
  'buying_session_scenarios',
  // Feedback
  'feedback_items',
  // Config (operator-managed) — domain_scope_config removed at the U10 slice-B
  // dsc drop (20260719000045).
  'po_types',
  'params',
  'cost_line_items',
  'buy_score_weight_components',
  'fulfillment_path_config',
  'notification_preferences',
  'organization',
];

// Config tables get their actor-UUID columns (any column ending in `_by`)
// nulled during serialization — Sol Checkpoint #1 Q2 defense-in-depth so a
// leaked export can't be used to enumerate user UUIDs. Operator loses
// UUID-only context (no profile names attached in the export anyway).
const CONFIG_TABLE_SET = new Set<ExportTableName>([
  'po_types',
  'params',
  'cost_line_items',
  'buy_score_weight_components',
  'fulfillment_path_config',
  'notification_preferences',
  'organization',
]);

function sanitizeConfigRow(row: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    result[k] = /_by$/.test(k) ? null : v;
  }
  return result;
}

interface ExportProgress {
  current: number;
  total: number;
  table: string;
}

function DataSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  const [exporting, setExporting] = useState<boolean>(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);

  const [resetOpen, setResetOpen] = useState<boolean>(false);
  const [resetInput, setResetInput] = useState<string>('');

  async function exportAll(): Promise<void> {
    setExporting(true);
    setProgress({ current: 0, total: EXPORT_ALLOWLIST.length, table: '' });
    const PAGE_SIZE = 1000;
    const out: Record<string, unknown[]> = {};

    try {
      for (let i = 0; i < EXPORT_ALLOWLIST.length; i++) {
        const table = EXPORT_ALLOWLIST[i];
        setProgress({ current: i + 1, total: EXPORT_ALLOWLIST.length, table });

        const rows: Record<string, unknown>[] = [];
        let offset = 0;
        for (;;) {
          // `table` is the full ExportTableName union; the typed client otherwise
          // distributes .from() across every table, exceeding TS's instantiation
          // depth as the schema grows (TS2589, surfaced when Day-100 added
          // docs_pages + release_notes). Cast to never to collapse the
          // distribution — rows are read generically as Record<string, unknown>[]
          // just below, so the per-table row type is unused here anyway.
          const { data, error } = await supabase
            .from(table as never)
            .select('*')
            .range(offset, offset + PAGE_SIZE - 1);
          if (error) {
            throw new Error(`Failed to fetch ${table} — ${error.message}`);
          }
          const page = (data ?? []) as Record<string, unknown>[];
          if (page.length === 0) break;
          rows.push(...page);
          if (page.length < PAGE_SIZE) break;
          offset += PAGE_SIZE;
        }

        out[table] = CONFIG_TABLE_SET.has(table) ? rows.map(sanitizeConfigRow) : rows;
      }

      const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `arbi-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast({ kind: 'success', message: 'Data export downloaded' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Export failed';
      showToast({ kind: 'error', message: msg });
    } finally {
      setExporting(false);
      setProgress(null);
    }
  }

  function openReset(): void {
    setResetInput('');
    setResetOpen(true);
  }

  function closeReset(): void {
    setResetOpen(false);
    setResetInput('');
  }

  function confirmReset(): void {
    // Per Day 92b prompt scope: terminal action stays inert at V1; the gate
    // UX ships but the destructive op is not implemented. Eventual
    // implementation's preserve-list per BACKLOG L3023.
    closeReset();
    showToast({ kind: 'info', message: 'Reset is not yet available' });
  }

  const resetEnabled = resetInput.trim().toUpperCase() === 'RESET';

  return (
    <SectionCard id="data" title="Data" sectionRefs={sectionRefs}>
      <div className="border border-danger/30 rounded-lg p-4 mt-1">
        <p className="text-2xs font-medium uppercase tracking-wider text-danger mb-3">
          Danger Zone
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ink-primary">Export all data</p>
              <p className="text-xs text-ink-muted">
                {exporting && progress
                  ? `Exporting ${progress.current} / ${progress.total} — ${progress.table}…`
                  : 'Download a full backup as JSON (50 tables; api_keys + audit log + PII excluded).'}
              </p>
            </div>
            <button
              onClick={() => void exportAll()}
              disabled={exporting}
              className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1.5 disabled:opacity-50"
              type="button"
            >
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          </div>
          <div className="border-t border-border-subtle" />
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ink-primary">Reset to defaults</p>
              <p className="text-xs text-ink-muted">
                Reset all transactional data to factory defaults
              </p>
            </div>
            <button
              onClick={openReset}
              className="arbi-btn-ghost text-xs text-danger hover:bg-danger/10 px-3 py-1.5"
              type="button"
            >
              <AlertTriangle size={12} /> Reset
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={resetOpen}
        title="Reset to defaults"
        message="This will reset transactional data — POs, receiving, inventory, returns, transfers, fulfillment. Settings config, params, PO Types, marketplace/path config, notification prefs, and user accounts are preserved. This cannot be undone."
        confirmLabel="Reset all data"
        confirmDisabled={!resetEnabled}
        onConfirm={confirmReset}
        onCancel={closeReset}
        extraContent={
          <div className="space-y-1.5 pt-1">
            <label htmlFor="reset-confirm" className="text-xs text-ink-secondary block">
              Type <span className="font-mono text-ink-bright">RESET</span> to confirm
            </label>
            <input
              id="reset-confirm"
              type="text"
              value={resetInput}
              onChange={(e) => setResetInput(e.target.value)}
              className="arbi-input w-full text-sm font-mono"
              placeholder="RESET"
              autoCapitalize="characters"
            />
          </div>
        }
      />
    </SectionCard>
  );
}

// ── Day 93 P7.7 — Settings Stub Sections Round 2 ────────────────────────────
// Ten admin-gated section components scaffolding 10 integration-shaped surfaces.
// Per Sol Checkpoint #1 GREEN (2026-05-28) + 3-round Phase 0 reconciliation lock.
// Bucketing: (a) integration_config ×6 + (b) dedicated tables ×2 + (c) inert ×2.
// All sections viewer-local fetch — useSettingsConfig NOT extended (admin-only,
// no cross-Settings consumers per Day 92a/92b precedent).

// ── Day 93 (a)-shell helper ─────────────────────────────────────────────────
// Hybrid of AlgopixSection (Type-3 SectionCard + status-pill layout) and the
// retired DomainScopeSection's ArbiToggle write-on-change + setBusy mechanics
// (dsc dropped at U10 slice B; the pattern lives on here). Each
// (a)-section is a thin wrapper that mounts this helper with its integration_key
// + optional renderExtra slot. integration_config.config writes via Template 13
// smart-spread mechanic — preserve other config fields on partial update. (No
// provenance trigger here; spread mechanic only, NOT IS-DISTINCT-FROM
// machinery — integration_config.config is a plain settings bag.)

const INTEGRATION_STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  not_configured: { label: 'Not configured', cls: 'bg-surface-4 text-ink-muted' },
  pending: { label: 'Pending', cls: 'bg-warning/15 text-warning' },
  connected: { label: 'Connected', cls: 'bg-success/15 text-success' },
  invalid: { label: 'Invalid', cls: 'bg-danger/15 text-danger' },
  error: { label: 'Error', cls: 'bg-danger/15 text-danger' },
};

interface IntegrationCardShellProps {
  sectionId: SectionId;
  title: string;
  integrationKey: string;
  integrationName: string;
  sectionRefs: SectionRefs;
  showToast: (t: ToastState) => void;
  renderExtra?: (
    row: IntegrationConfig,
    setConfigField: (key: string, value: unknown) => Promise<void>,
    busy: boolean
  ) => ReactNode;
}

function IntegrationCardShell({
  sectionId,
  title,
  integrationKey,
  integrationName,
  sectionRefs,
  showToast,
  renderExtra,
}: IntegrationCardShellProps): JSX.Element {
  const [row, setRow] = useState<IntegrationConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await supabase
      .from('integration_config')
      .select('*')
      .eq('integration_key', integrationKey)
      .maybeSingle();
    if (error) {
      showToast({ kind: 'error', message: `Failed to load ${title} — ${error.message}` });
      setLoading(false);
      return;
    }
    setRow((data as unknown as IntegrationConfig | null) ?? null);
    setLoading(false);
  }, [integrationKey, showToast, title]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function toggleEnabled(next: boolean): Promise<void> {
    if (!row) return;
    setBusy(true);
    const { error } = await supabase
      .from('integration_config')
      .update({ is_enabled: next } as never)
      .eq('id', row.id);
    setBusy(false);
    if (error) {
      showToast({ kind: 'error', message: `Failed to update — ${error.message}` });
      return;
    }
    setRow({ ...row, is_enabled: next });
    showToast({ kind: 'success', message: `${title} ${next ? 'enabled' : 'disabled'}` });
  }

  async function setConfigField(key: string, value: unknown): Promise<void> {
    if (!row) return;
    setBusy(true);
    // Template 13 smart-spread mechanic — preserve other config fields when
    // writing a partial config patch. Without this, a naive
    // `update({ config: { [key]: value } })` would clobber sibling fields.
    const prev = (row.config as Record<string, unknown> | null) ?? {};
    const newConfig: Record<string, unknown> = { ...prev, [key]: value };
    const { error } = await supabase
      .from('integration_config')
      .update({ config: newConfig } as never)
      .eq('id', row.id);
    setBusy(false);
    if (error) {
      showToast({ kind: 'error', message: `Failed to update — ${error.message}` });
      return;
    }
    setRow({ ...row, config: newConfig as IntegrationConfig['config'] });
    showToast({ kind: 'success', message: `${title} updated` });
  }

  const badge =
    INTEGRATION_STATUS_BADGE[row?.connection_status ?? 'not_configured'] ??
    INTEGRATION_STATUS_BADGE.not_configured;

  return (
    <SectionCard id={sectionId} title={title} sectionRefs={sectionRefs}>
      {loading ? (
        <p className="text-xs text-ink-muted">Loading…</p>
      ) : !row ? (
        <p className="text-xs text-danger">
          Configuration row missing for {integrationKey}. Re-seed integration_config.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className={`px-2 py-0.5 rounded text-2xs font-medium ${badge.cls}`}>
              {badge.label}
            </span>
            <ArbiToggle
              checked={row.is_enabled}
              onChange={() => void toggleEnabled(!row.is_enabled)}
              label={`Toggle ${title} enabled`}
              disabled={busy}
            />
          </div>
          {renderExtra && renderExtra(row, setConfigField, busy)}
          <p className="text-xs text-ink-muted">
            Credentials and connection setup are configured when the {integrationName} integration
            ships.
          </p>
        </div>
      )}
    </SectionCard>
  );
}

// ── Day 93 G.4 — Repricer (a) ───────────────────────────────────────────────

function RepricerSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  return (
    <IntegrationCardShell
      sectionId="repricer"
      title="Repricer"
      integrationKey="repricer"
      integrationName="Repricer"
      sectionRefs={sectionRefs}
      showToast={showToast}
      renderExtra={(row, setConfigField, busy) => {
        const cfg = (row.config as { respect_manual_overrides?: boolean } | null) ?? {};
        const respect = cfg.respect_manual_overrides ?? true;
        return (
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-border-subtle">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-ink-bright">Respect manual price overrides</p>
              <p className="text-2xs text-ink-muted mt-0.5">
                When the Repricer is connected, it will not overwrite price overrides you have set
                manually.
              </p>
            </div>
            <ArbiToggle
              checked={respect}
              onChange={() => void setConfigField('respect_manual_overrides', !respect)}
              label="Toggle respect manual price overrides"
              disabled={busy}
            />
          </div>
        );
      }}
    />
  );
}

// ── Day 93 G.5 — BLVS API (a) ───────────────────────────────────────────────

function BlvsApiSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  return (
    <IntegrationCardShell
      sectionId="blvs-api"
      title="BLVS API"
      integrationKey="blvs_api"
      integrationName="BLVS API"
      sectionRefs={sectionRefs}
      showToast={showToast}
    />
  );
}

// ── Day 93 G.6 — Keepa (a) ──────────────────────────────────────────────────

function KeepaSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  return (
    <IntegrationCardShell
      sectionId="keepa"
      title="Keepa"
      integrationKey="keepa"
      integrationName="Keepa"
      sectionRefs={sectionRefs}
      showToast={showToast}
    />
  );
}

// ── Day 93 G.13 — Multi-channel Sync (a) ────────────────────────────────────

function MultichannelSyncSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  return (
    <IntegrationCardShell
      sectionId="multichannel"
      title="Multi-channel Sync"
      integrationKey="multichannel_sync"
      integrationName="Multi-channel Sync"
      sectionRefs={sectionRefs}
      showToast={showToast}
    />
  );
}

// ── Day 93 G.14 — Shipping (a) ──────────────────────────────────────────────

function ShippingSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  return (
    <IntegrationCardShell
      sectionId="shipping"
      title="Shipping"
      integrationKey="shipping"
      integrationName="Shipping"
      sectionRefs={sectionRefs}
      showToast={showToast}
    />
  );
}

// ── Day 93 G.15 — Accounting (a) ────────────────────────────────────────────

function AccountingSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  return (
    <IntegrationCardShell
      sectionId="accounting"
      title="Accounting"
      integrationKey="accounting"
      integrationName="Accounting"
      sectionRefs={sectionRefs}
      showToast={showToast}
    />
  );
}

// ── Day 93 G.7 — Documents & Templates (b) ──────────────────────────────────
// Mirror POTypesSection (Day 92b) — list + per-row Edit + soft-delete via
// is_active toggle + Add/Edit modal. template_key auto-slugified on CREATE,
// immutable on EDIT (stable-handle for downstream rendering consumers).

function slugifyTemplateName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const TEMPLATE_CATEGORY_OPTIONS: ArbiSelectOption<string>[] = [
  { value: 'po', label: 'Purchase Order' },
  { value: 'email', label: 'Email' },
  { value: 'listing_export', label: 'Listing Export' },
  { value: 'misc', label: 'Miscellaneous' },
];

const TEMPLATE_CATEGORY_LABEL: Record<string, string> = {
  po: 'PO',
  email: 'Email',
  listing_export: 'Listing Export',
  misc: 'Misc',
};

type TemplateModalMode = 'add' | 'edit';

interface DocumentTemplateDraft {
  display_name: string;
  category: string;
  content: string;
}

const EMPTY_TEMPLATE_DRAFT: DocumentTemplateDraft = {
  display_name: '',
  category: 'po',
  content: '',
};

function templateDraftFromRow(row: DocumentTemplate): DocumentTemplateDraft {
  return {
    display_name: row.display_name,
    category: row.category,
    content: row.content ?? '',
  };
}

function DocumentTemplatesSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  const [rows, setRows] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<TemplateModalMode>('add');
  const [modalRow, setModalRow] = useState<DocumentTemplate | null>(null);
  const [draft, setDraft] = useState<DocumentTemplateDraft>(EMPTY_TEMPLATE_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const loadRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await supabase
      .from('document_templates')
      .select('*')
      .order('display_name');
    if (error) {
      showToast({ kind: 'error', message: `Failed to load templates — ${error.message}` });
      setLoading(false);
      return;
    }
    setRows((data as unknown as DocumentTemplate[]) ?? []);
    setLoading(false);
  }, [showToast]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (modalOpen && nameInputRef.current) nameInputRef.current.focus();
  }, [modalOpen]);

  function openAdd(): void {
    setModalMode('add');
    setModalRow(null);
    setDraft(EMPTY_TEMPLATE_DRAFT);
    setDraftError(null);
    setModalOpen(true);
  }

  function openEdit(row: DocumentTemplate): void {
    setModalMode('edit');
    setModalRow(row);
    setDraft(templateDraftFromRow(row));
    setDraftError(null);
    setModalOpen(true);
  }

  function closeModal(): void {
    if (busy) return;
    setModalOpen(false);
    setDraft(EMPTY_TEMPLATE_DRAFT);
    setDraftError(null);
  }

  async function submitDraft(): Promise<void> {
    const name = draft.display_name.trim();
    if (!name) {
      setDraftError('Enter a name for this template');
      return;
    }

    setBusy(true);
    setDraftError(null);

    if (modalMode === 'add') {
      const templateKey = slugifyTemplateName(name);
      if (!templateKey) {
        setDraftError('Name must contain at least one letter or number');
        setBusy(false);
        return;
      }
      const { data: existing } = await supabase
        .from('document_templates')
        .select('id')
        .eq('template_key', templateKey)
        .maybeSingle();
      if (existing) {
        setDraftError(
          `A template with the handle "${templateKey}" already exists. Try a different name.`
        );
        setBusy(false);
        return;
      }
      const { error } = await supabase.from('document_templates').insert({
        template_key: templateKey,
        display_name: name,
        category: draft.category,
        content: draft.content,
      } as never);
      setBusy(false);
      if (error) {
        setDraftError(`Failed to create — ${error.message}`);
        return;
      }
      showToast({ kind: 'success', message: `Template "${name}" created` });
    } else {
      if (!modalRow) {
        setBusy(false);
        return;
      }
      // template_key omitted from UPDATE — immutable handle preserves
      // downstream rendering consumer lookups (mirror Day 92b POTypesSection
      // slug strategy + stable-handle backlog intent).
      const { error } = await supabase
        .from('document_templates')
        .update({
          display_name: name,
          category: draft.category,
          content: draft.content,
        } as never)
        .eq('id', modalRow.id);
      setBusy(false);
      if (error) {
        setDraftError(`Failed to save — ${error.message}`);
        return;
      }
      showToast({ kind: 'success', message: 'Template updated' });
    }

    setModalOpen(false);
    setDraft(EMPTY_TEMPLATE_DRAFT);
    setDraftError(null);
    await loadRows();
  }

  async function toggleActive(id: string, currentActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('document_templates')
      .update({ is_active: !currentActive } as never)
      .eq('id', id);
    if (error) {
      showToast({ kind: 'error', message: error.message });
      return;
    }
    await loadRows();
    showToast({ kind: 'success', message: 'Template updated' });
  }

  function update<K extends keyof DocumentTemplateDraft>(
    key: K,
    value: DocumentTemplateDraft[K]
  ): void {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  const previewSlug =
    modalMode === 'add' && draft.display_name.trim() ? slugifyTemplateName(draft.display_name) : '';

  return (
    <SectionCard id="docs-templates" title="Documents & Templates" sectionRefs={sectionRefs}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs text-ink-muted">
          PO, email, and export template scaffolds. Rich editor and consumer-side rendering (PO PDF
          generation, etc.) ship in the consumer sessions.
        </p>
        <button
          onClick={openAdd}
          className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5"
          type="button"
        >
          Add template
        </button>
      </div>
      {loading ? (
        <p className="text-xs text-ink-muted py-4">Loading…</p>
      ) : (
        <div className="space-y-2">
          {rows.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between py-2.5 px-2 bg-surface-2 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-primary">{t.display_name}</span>
                  <span className="font-mono text-2xs text-ink-muted">{t.template_key}</span>
                  <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-info/15 text-info">
                    {TEMPLATE_CATEGORY_LABEL[t.category] ?? t.category}
                  </span>
                </div>
                <p className="text-2xs text-ink-muted mt-0.5">
                  {t.content
                    ? `${t.content.slice(0, 80)}${t.content.length > 80 ? '…' : ''}`
                    : '(empty)'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => openEdit(t)}
                  className="arbi-btn-ghost text-xs px-2 py-1"
                  type="button"
                >
                  Edit
                </button>
                <ArbiToggle
                  checked={t.is_active}
                  onChange={() => toggleActive(t.id, t.is_active)}
                  label={`Toggle ${t.display_name}`}
                />
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <p className="text-sm text-ink-muted text-center py-4">No templates configured</p>
          )}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} aria-hidden="true" />
          <div className="relative bg-surface-2 border border-border rounded-lg shadow-xl w-[32rem] p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-sm font-semibold text-ink-bright">
              {modalMode === 'add'
                ? 'Add template'
                : `Edit ${modalRow?.display_name ?? 'template'}`}
            </h3>

            <div className="space-y-1.5">
              <label htmlFor="doc-template-name" className="text-xs text-ink-secondary block">
                Name
              </label>
              <input
                ref={nameInputRef}
                id="doc-template-name"
                type="text"
                value={draft.display_name}
                onChange={(e) => update('display_name', e.target.value)}
                placeholder="e.g. Purchase Order PDF"
                className="arbi-input w-full text-sm"
                disabled={busy}
              />
              {modalMode === 'add' && previewSlug && (
                <p className="text-2xs text-ink-muted">
                  Handle: <span className="font-mono text-ink-secondary">{previewSlug}</span>{' '}
                  (auto-generated; cannot be changed later)
                </p>
              )}
              {modalMode === 'edit' && modalRow && (
                <p className="text-2xs text-ink-muted">
                  Handle:{' '}
                  <span className="font-mono text-ink-secondary">{modalRow.template_key}</span>{' '}
                  (immutable)
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="doc-template-category" className="text-xs text-ink-secondary block">
                Category
              </label>
              <ArbiSelect
                value={draft.category}
                options={TEMPLATE_CATEGORY_OPTIONS}
                onChange={(v) => update('category', v)}
                size="sm"
                className="w-48"
                disabled={busy}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="doc-template-content" className="text-xs text-ink-secondary block">
                Content
              </label>
              <textarea
                id="doc-template-content"
                value={draft.content}
                onChange={(e) => update('content', e.target.value)}
                placeholder="Template body. Rich editing ships in the consumer session."
                className="arbi-input w-full text-sm font-mono resize-y"
                rows={8}
                disabled={busy}
              />
            </div>

            {draftError && (
              <p className="text-xs text-danger" role="alert">
                {draftError}
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={closeModal}
                disabled={busy}
                className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1.5 disabled:opacity-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={() => void submitDraft()}
                disabled={busy || !draft.display_name.trim()}
                className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5 disabled:opacity-50"
                type="button"
              >
                {busy ? 'Saving…' : modalMode === 'add' ? 'Create template' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ── Day 93 G.16 — Webhooks (b) ──────────────────────────────────────────────
// Mirror POTypesSection Add/Edit shape. event_types is FREE-TEXT array input
// (no event registry yet; mirror cost_line_items.applies_to_paths text[]
// semantic). secret column is rendered disabled with placeholder copy —
// deferred to the inbound webhook handler session per the credential-at-rest
// posture lock.

function parseEventTypes(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function formatEventTypes(arr: string[] | null | undefined): string {
  return (arr ?? []).join('\n');
}

type WebhookModalMode = 'add' | 'edit';

interface WebhookDraft {
  url: string;
  event_types_text: string;
  is_active: boolean;
}

const EMPTY_WEBHOOK_DRAFT: WebhookDraft = {
  url: '',
  event_types_text: '',
  is_active: true,
};

function webhookDraftFromRow(row: WebhookEndpoint): WebhookDraft {
  return {
    url: row.url,
    event_types_text: formatEventTypes(row.event_types),
    is_active: row.is_active,
  };
}

function WebhooksSection({ sectionRefs, showToast }: SectionWithToastProps): JSX.Element {
  const [rows, setRows] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<WebhookModalMode>('add');
  const [modalRow, setModalRow] = useState<WebhookEndpoint | null>(null);
  const [draft, setDraft] = useState<WebhookDraft>(EMPTY_WEBHOOK_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);
  const urlInputRef = useRef<HTMLInputElement | null>(null);

  const loadRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await supabase
      .from('webhook_endpoints')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      showToast({ kind: 'error', message: `Failed to load webhooks — ${error.message}` });
      setLoading(false);
      return;
    }
    setRows((data as unknown as WebhookEndpoint[]) ?? []);
    setLoading(false);
  }, [showToast]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (modalOpen && urlInputRef.current) urlInputRef.current.focus();
  }, [modalOpen]);

  function openAdd(): void {
    setModalMode('add');
    setModalRow(null);
    setDraft(EMPTY_WEBHOOK_DRAFT);
    setDraftError(null);
    setModalOpen(true);
  }

  function openEdit(row: WebhookEndpoint): void {
    setModalMode('edit');
    setModalRow(row);
    setDraft(webhookDraftFromRow(row));
    setDraftError(null);
    setModalOpen(true);
  }

  function closeModal(): void {
    if (busy) return;
    setModalOpen(false);
    setDraft(EMPTY_WEBHOOK_DRAFT);
    setDraftError(null);
  }

  async function submitDraft(): Promise<void> {
    const url = draft.url.trim();
    if (!url) {
      setDraftError('Enter a URL for this webhook');
      return;
    }
    try {
      // Light URL syntax check; full validation is the handler's job.
      new URL(url);
    } catch {
      setDraftError('Enter a valid URL (including the protocol scheme)');
      return;
    }
    const eventTypes = parseEventTypes(draft.event_types_text);

    setBusy(true);
    setDraftError(null);

    if (modalMode === 'add') {
      const { error } = await supabase.from('webhook_endpoints').insert({
        url,
        event_types: eventTypes,
        is_active: draft.is_active,
      } as never);
      setBusy(false);
      if (error) {
        setDraftError(`Failed to create — ${error.message}`);
        return;
      }
      showToast({ kind: 'success', message: 'Webhook endpoint created' });
    } else {
      if (!modalRow) {
        setBusy(false);
        return;
      }
      const { error } = await supabase
        .from('webhook_endpoints')
        .update({
          url,
          event_types: eventTypes,
          is_active: draft.is_active,
        } as never)
        .eq('id', modalRow.id);
      setBusy(false);
      if (error) {
        setDraftError(`Failed to save — ${error.message}`);
        return;
      }
      showToast({ kind: 'success', message: 'Webhook endpoint updated' });
    }

    setModalOpen(false);
    setDraft(EMPTY_WEBHOOK_DRAFT);
    setDraftError(null);
    await loadRows();
  }

  async function toggleActive(id: string, currentActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('webhook_endpoints')
      .update({ is_active: !currentActive } as never)
      .eq('id', id);
    if (error) {
      showToast({ kind: 'error', message: error.message });
      return;
    }
    await loadRows();
    showToast({ kind: 'success', message: 'Webhook endpoint updated' });
  }

  function update<K extends keyof WebhookDraft>(key: K, value: WebhookDraft[K]): void {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <SectionCard id="webhooks" title="Webhooks" sectionRefs={sectionRefs}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs text-ink-muted">
          Operator-configured webhook endpoints. The signing secret and delivery pipeline ship in
          the inbound webhook handler session.
        </p>
        <button
          onClick={openAdd}
          className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5"
          type="button"
        >
          Add endpoint
        </button>
      </div>
      {loading ? (
        <p className="text-xs text-ink-muted py-4">Loading…</p>
      ) : (
        <div className="space-y-2">
          {rows.map((w) => (
            <div
              key={w.id}
              className="flex items-center justify-between py-2.5 px-2 bg-surface-2 rounded-lg gap-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-mono text-ink-primary truncate" title={w.url}>
                  {w.url}
                </p>
                <div className="flex items-center flex-wrap gap-1 mt-1">
                  {(w.event_types ?? []).length === 0 ? (
                    <span className="text-2xs text-ink-muted">(no events subscribed)</span>
                  ) : (
                    (w.event_types ?? []).map((et) => (
                      <span
                        key={et}
                        className="px-1.5 py-0.5 rounded text-2xs font-medium bg-info/15 text-info font-mono"
                      >
                        {et}
                      </span>
                    ))
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => openEdit(w)}
                  className="arbi-btn-ghost text-xs px-2 py-1"
                  type="button"
                >
                  Edit
                </button>
                <ArbiToggle
                  checked={w.is_active}
                  onChange={() => toggleActive(w.id, w.is_active)}
                  label="Toggle webhook"
                />
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <p className="text-sm text-ink-muted text-center py-4">
              No webhook endpoints configured
            </p>
          )}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} aria-hidden="true" />
          <div className="relative bg-surface-2 border border-border rounded-lg shadow-xl w-[32rem] p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-sm font-semibold text-ink-bright">
              {modalMode === 'add' ? 'Add webhook endpoint' : 'Edit webhook endpoint'}
            </h3>

            <div className="space-y-1.5">
              <label htmlFor="webhook-url" className="text-xs text-ink-secondary block">
                URL
              </label>
              <input
                ref={urlInputRef}
                id="webhook-url"
                type="text"
                value={draft.url}
                onChange={(e) => update('url', e.target.value)}
                placeholder="https://example.com/arbi-webhook"
                className="arbi-input w-full text-sm font-mono"
                disabled={busy}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="webhook-events" className="text-xs text-ink-secondary block">
                Subscribed event types
              </label>
              <textarea
                id="webhook-events"
                value={draft.event_types_text}
                onChange={(e) => update('event_types_text', e.target.value)}
                placeholder={
                  'one event type per line, or comma-separated\n(e.g. po.created, receiving.submitted)'
                }
                className="arbi-input w-full text-sm font-mono resize-y"
                rows={4}
                disabled={busy}
              />
              <p className="text-2xs text-ink-muted">
                Free-text at V1 — there is no event registry yet. The inbound handler session will
                promote to a typed multi-select against the real event registry.
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="webhook-secret" className="text-xs text-ink-secondary block">
                Signing secret
              </label>
              <input
                id="webhook-secret"
                type="text"
                value=""
                disabled
                placeholder="Signing secret is managed in the inbound webhook handler."
                className="arbi-input w-full text-sm font-mono opacity-50 cursor-not-allowed"
              />
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
              <span className="text-xs text-ink-secondary">Active</span>
              <ArbiToggle
                checked={draft.is_active}
                onChange={() => update('is_active', !draft.is_active)}
                label="Toggle is_active"
                disabled={busy}
              />
            </div>

            {draftError && (
              <p className="text-xs text-danger" role="alert">
                {draftError}
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={closeModal}
                disabled={busy}
                className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1.5 disabled:opacity-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={() => void submitDraft()}
                disabled={busy || !draft.url.trim()}
                className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5 disabled:opacity-50"
                type="button"
              >
                {busy ? 'Saving…' : modalMode === 'add' ? 'Create endpoint' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ── Day 93 G.2 — Rules Engine (c) read-only documentation ───────────────────
// Hardcoded documentation constant sourced from supabase/migrations/
// 20260429000002_rules_engine.sql (Day 51 P1 dispatcher). The 4 rule handlers
// are LIVE (bodies in sibling migrations 0003/4/5/6) — NOT stubs at V1. The
// section reads this constant; no live query, no rules-registry table. When
// the dispatcher grows beyond ~15 rules per ADR Decision 20, this section's
// content will be promoted to an arbi-dev config-driven view.

interface RulesEngineEvent {
  eventType: string;
  description: string;
  handlers: Array<{ branch: string; handler: string; noOp?: boolean }>;
}

const RULES_ENGINE_EVENTS: RulesEngineEvent[] = [
  {
    eventType: 'receiving_submitted_with_discrepancy',
    description:
      'Receiving record submitted with one or more discrepancies — creates a Discrepancies-queue task.',
    handlers: [
      {
        branch: '—',
        handler: 'rule_create_discrepancies_task(po_id, po_number, discrepancy_count)',
      },
    ],
  },
  {
    eventType: 'discrepancy_resolved',
    description: 'Discrepancy resolved — dispatcher branches on the resolution kind.',
    handlers: [
      {
        branch: "resolution = 'write_off'",
        handler: 'rule_create_finance_writeoff(line_item_id, resolution_id, po_id)',
      },
      {
        branch: "resolution = 'return_to_distributor'",
        handler: 'rule_create_transfers_removal(line_item_id, resolution_id, po_id)',
      },
      {
        branch: "resolution = 'accept_shortage'",
        handler: 'no side effects',
        noOp: true,
      },
      {
        branch: "resolution = 'request_replacement'",
        handler: 'no side effects',
        noOp: true,
      },
    ],
  },
  {
    eventType: 'invoice_variance_detected',
    description: 'Invoice variance exceeds tolerance — flags the invoice + writes a variance card.',
    handlers: [
      {
        branch: '—',
        handler: 'rule_flag_invoice_variance(invoice_id, po_id, variance_pct)',
      },
    ],
  },
];

function RulesEngineSection({ sectionRefs }: SectionRefsOnlyProps): JSX.Element {
  return (
    <SectionCard id="rules-engine" title="Rules Engine" sectionRefs={sectionRefs}>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded text-2xs font-medium bg-success/15 text-success">
            Active (V1 dispatcher)
          </span>
        </div>
        <p className="text-xs text-ink-secondary">
          V1 is a server-side plpgsql dispatcher (ADR Decision 20). Every workflow trigger passes
          through{' '}
          <code className="font-mono text-2xs">public.evaluate_rules(event_type, context)</code>{' '}
          (Architecture Rule #2). The dispatcher routes by event type to dedicated rule handlers.
          Configuration is hard-coded at V1; arbi-dev replaces with a config-driven version when the
          rule count exceeds ~15 (post-launch).
        </p>
        <div className="space-y-2">
          {RULES_ENGINE_EVENTS.map((evt) => (
            <div key={evt.eventType} className="py-2 px-3 bg-surface-2 rounded-lg">
              <p className="text-xs font-mono text-ink-bright">{evt.eventType}</p>
              <p className="text-2xs text-ink-muted mt-0.5">{evt.description}</p>
              <ul className="mt-1.5 space-y-0.5">
                {evt.handlers.map((h, ix) => (
                  <li
                    key={ix}
                    className={`text-2xs ${h.noOp ? 'text-ink-muted' : 'text-ink-secondary'}`}
                  >
                    <span className="font-mono">{h.branch}</span>
                    {' → '}
                    <span className="font-mono">{h.handler}</span>
                    {h.noOp && (
                      <span className="ml-2 px-1 py-0.5 rounded text-2xs bg-surface-4 text-ink-muted">
                        no-op
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-2xs text-ink-muted">
          Source of truth:{' '}
          <code className="font-mono">supabase/migrations/20260429000002_rules_engine.sql</code>{' '}
          (dispatcher) + sibling migrations <code className="font-mono">0003</code>/
          <code className="font-mono">0004</code>/<code className="font-mono">0005</code>/
          <code className="font-mono">0006</code> (handler bodies). Unknown event types fire{' '}
          <code className="font-mono">RAISE WARNING</code>.
        </p>
      </div>
    </SectionCard>
  );
}

// ── Day 93 G.12 — Billing (c) static informational ──────────────────────────
// AlgopixSection layout minus the toggle. No substrate, no external link.

function BillingSection({ sectionRefs }: SectionRefsOnlyProps): JSX.Element {
  return (
    <SectionCard id="billing" title="Billing" sectionRefs={sectionRefs}>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded text-2xs font-medium bg-info/15 text-info">
            Managed externally
          </span>
        </div>
        <p className="text-sm text-ink-secondary">
          Subscription and tool-cost tracking are managed outside Arbi at V1.
        </p>
      </div>
    </SectionCard>
  );
}

// ── Main Settings page ────────────────────────────────────────────────────────

export default function Settings(): JSX.Element {
  const store = useParamStore();
  const { isAdmin } = useApp();
  const [activeSection, setActiveSection] = useState<SectionId>('general');
  const [toast, setToast] = useState<ToastState | null>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);

  const showToast = useCallback((t: ToastState): void => setToast(t), []);

  // IntersectionObserver to track which section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the first visible section from the top
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          const id = visible[0].target.id.replace('section-', '') as SectionId;
          setActiveSection(id);
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    observerRef.current = observer;

    // Observe all section elements
    const timeout = setTimeout(() => {
      Object.values(sectionRefs.current).forEach((el) => {
        if (el) observer.observe(el);
      });
    }, 100);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [store.loading]);

  function scrollToSection(id: SectionId): void {
    const el = sectionRefs.current[id] || document.getElementById(`section-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  }

  return (
    <div className="h-full overflow-auto">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <div className="max-w-5xl mx-auto p-5">
        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-ink-bright flex items-center gap-2">
            <Settings2 size={18} className="text-accent" />
            Settings
          </h1>
          <p className="text-sm text-ink-muted mt-0.5">
            Manage your organization, business rules, and integrations
          </p>
        </div>

        <div className="flex gap-6">
          {/* Left panel — sticky nav */}
          <nav className="w-48 flex-shrink-0">
            <div className="sticky top-5 space-y-4">
              {NAV_GROUPS.map((group, gi) => {
                // Filter out admin-only sections for non-admin users.
                const visibleItems = group.items.filter((item) => {
                  if (isAdminOnlySection(item.id) && !isAdmin) return false;
                  return true;
                });
                if (visibleItems.length === 0) return null;
                return (
                  <div key={group.label}>
                    {gi > 0 && <div className="border-t border-border-subtle mb-3" />}
                    <p className="text-2xs uppercase tracking-wider text-ink-muted mb-2 px-2">
                      {group.label}
                    </p>
                    <div className="space-y-0.5">
                      {visibleItems.map((item) => {
                        const isActive = activeSection === item.id;
                        return (
                          <button
                            key={item.id}
                            onClick={() => scrollToSection(item.id)}
                            aria-current={isActive ? 'page' : undefined}
                            aria-label={item.label}
                            className={clsx(
                              'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors text-left',
                              isActive
                                ? 'border-l-2 border-accent text-accent bg-accent/10'
                                : 'text-ink-secondary hover:text-ink-primary hover:bg-surface-3'
                            )}
                          >
                            <item.icon
                              size={14}
                              className={isActive ? 'text-accent' : 'text-ink-muted'}
                            />
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Right content — scrolls */}
          <div className="flex-1 min-w-0 space-y-6 pb-12">
            {/* ORGANIZATION */}
            <ProfileSection sectionRefs={sectionRefs} showToast={showToast} />

            <GeneralSection sectionRefs={sectionRefs} showToast={showToast} />

            {isAdmin && <SecuritySection sectionRefs={sectionRefs} showToast={showToast} />}

            {/* Day 93 P7.7 G.12 — Billing (c) — static informational placeholder */}
            {isAdmin && <BillingSection sectionRefs={sectionRefs} />}

            <NotificationsSection sectionRefs={sectionRefs} />

            {/* BUSINESS RULES */}
            <ParamsSection store={store} sectionRefs={sectionRefs} showToast={showToast} />

            {isAdmin && <POTypesSection sectionRefs={sectionRefs} showToast={showToast} />}

            {/* Day 93 P7.7 G.2 — Rules Engine (c) — read-only documentation of
                the V1 plpgsql dispatcher per ADR Decision 20 + Architecture
                Rule #2. Replaces the prior StubSection at this slot. */}
            {isAdmin && <RulesEngineSection sectionRefs={sectionRefs} />}

            {/* Distributor Scope section RETIRED — U10 SLICE B (20260719000045). */}

            {/* U10 A2 (BACKLOG:315 [ANNOTATED AR-0145: DEAD at HEAD; anchor by content — "U10 A2 - the per-distributor capability registry editor"]a) — admin-gated per-distributor capability
                registry editor (is_supported + config per direction; the :306
                BLVS deferred-details discharge path). */}
            {isAdmin && (
              <DistributorCapabilitiesSection sectionRefs={sectionRefs} showToast={showToast} />
            )}

            {/* Day 87 P7.3 — admin-gated cost components surface (D.8 + A5=b
                independent distributor-mode toolbar; A3=a Edit + Toggle only;
                A4=a calculation_type read-only badge) */}
            {isAdmin && <CostLineItemsSection sectionRefs={sectionRefs} showToast={showToast} />}

            {/* Day 86 P7.2 — admin-gated channel + path activation surface */}
            {isAdmin && <ChannelsAndPathsSection sectionRefs={sectionRefs} showToast={showToast} />}

            {/* INTEGRATIONS */}
            {/* Fix Pass A (S-F-T3.6-10) — admin-gate the Marketplace APIs stub to
                match ADMIN_ONLY_SETTINGS + every sibling integration scaffold
                (previously rendered unguarded to non-admins). */}
            {isAdmin && (
              <StubSection
                id="marketplace-apis"
                title="Marketplace APIs"
                icon={Link2}
                message="Integration coming in Phase 3"
                sectionRefs={sectionRefs}
              />
            )}

            {/* Day 93 P7.7 G.13/G.14/G.15 — INTEGRATIONS (a) bucket scaffolds
                (Multi-channel Sync, Shipping, Accounting). Nav placement per
                CLAUDE.md §6 — between Marketplace APIs and Algopix. */}
            {isAdmin && <MultichannelSyncSection sectionRefs={sectionRefs} showToast={showToast} />}
            {isAdmin && <ShippingSection sectionRefs={sectionRefs} showToast={showToast} />}
            {isAdmin && <AccountingSection sectionRefs={sectionRefs} showToast={showToast} />}

            <AlgopixSection sectionRefs={sectionRefs} />

            {/* Day 93 P7.7 G.4/G.5/G.6 — INTEGRATIONS (a) bucket scaffolds
                replacing the prior anon-visible stubs; ADD {isAdmin} wrap. */}
            {isAdmin && <RepricerSection sectionRefs={sectionRefs} showToast={showToast} />}
            {isAdmin && <BlvsApiSection sectionRefs={sectionRefs} showToast={showToast} />}
            {isAdmin && <KeepaSection sectionRefs={sectionRefs} showToast={showToast} />}

            {/* SYSTEM */}
            {/* Day 93 P7.7 G.7 — Documents & Templates (b) — replaces prior
                anon-visible stub; ADD {isAdmin} wrap. */}
            {isAdmin && (
              <DocumentTemplatesSection sectionRefs={sectionRefs} showToast={showToast} />
            )}

            {isAdmin && <ApiKeysSection sectionRefs={sectionRefs} showToast={showToast} />}

            {/* Day 93 P7.7 G.16 — Webhooks (b) — nav placement per CLAUDE.md §6
                between API Keys and Audit Trail. */}
            {isAdmin && <WebhooksSection sectionRefs={sectionRefs} showToast={showToast} />}

            {isAdmin && <AuditTrailSection sectionRefs={sectionRefs} />}

            {isAdmin && <DataSection sectionRefs={sectionRefs} showToast={showToast} />}
          </div>
        </div>
      </div>
    </div>
  );
}
